import { TableColumn } from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { Href, MAX_SCORE } from '../../../../constants';
import {
  AjaxSourced,
  AllDBUsersLeaderboard,
  AllUsersLeaderboardOptimized,
  BotFilesData,
  CourseData,
  CourseLesson,
  CourseLessonWithDeleteFlag,
  CustomBadgeProp,
  EngagementsTableData,
  ExamData,
  ExamRequest,
  HtmlSourced,
  LeaderBoardData,
  LeadsTableData,
  ServerSideProcess,
  StatsStatusDetails,
  UserData,
  VideoData,
} from '../../../../types/Tables';
import { Badge, Button, UncontrolledTooltip } from 'reactstrap';
import { CardSubtitle } from '../../../../types/CoreComponents';
import { routes } from '../../../../routers/routes';
import { StudentCourse } from '../../../../types/Courses';
import { t, TFunction } from 'i18next';
import { User } from '../../../../types/Applications';
import { UserFeedback } from '../../../../types/Feedbacks';
import { AuthActivityProps } from '../../../../types/Dashboard';
import {
  AssistantExamAttempt,
  AssistantExamResponse,
  ExamFile,
  ExamHistoryItem,
  ScoringData,
} from '../../../../types/Exams';
import { formatWithOffset } from '../../../../lib/timeUtils';
import { LeadStageStatus, TaskType } from '../../../../types/Leads';

import { UserRole } from '../../../../types/Forms';
import {
  CrmControlActionsColors,
  CrmControlItem,
  CrmControlItemActionHistoryItem,
  OverdueItem,
  UnprocessedTranscriptionItem,
} from '../../../../types/Control';
import { Gallery, Item } from 'react-photoswipe-gallery';

import { Chip } from '../../../../components/uiKits/chip/Chip';
import { getTranslate } from '../../../../lib/translate';

export const HtmlTableSubTitle: CardSubtitle[] = [
  {
    text: 'The foundation for DataTables is progressive enhancement, so it is very adept at reading table information directly from the DOM. This example shows how easy it is to add searching, ordering and paging to your HTML table by simply running DataTables on it.',
  },
];

export const AjaxTableSubTitle: CardSubtitle[] = [
  {
    text: 'DataTables has the ability to read data from virtually any JSON data source that can be obtained by Ajax. This can be done, in its most simple form, by setting the',
    code: ' ajax:option ',
  },
  {
    text: 'option to the address of the JSON data source.',
  },
];

export const CoursesListTableSubtitle: CardSubtitle[] = [
  {
    text: 'Table - list of courses',
    code: 'some code',
  },
];

export const LessonsListTableTitle: string = 'Table of lessons';

export const LessonsListTableSubtitle: CardSubtitle[] = [
  {
    text: 'List of lessons',
  },
];

export const ExamListTableSubtitle: CardSubtitle[] = [
  {
    text: 'List of students for exam',
  },
];
export const ExamRequestListTableSubtitle: CardSubtitle[] = [
  {
    text: 'List of students for examination ',
  },
];
export const StudentsListTableSubtitle: CardSubtitle[] = [
  {
    text: 'Table - list of students',
    code: 'some code',
  },
];

export const VideoListTableSubtitle: CardSubtitle[] = [
  {
    text: 'Already used videos',
  },
];

export const JavaScriptTableSubTitle: CardSubtitle[] = [
  {
    text: 'At times you will wish to be able to create a table from dynamic information passed directly to DataTables, rather than having it read from the document. This is achieved using the',
    code: ' data ',
  },
  {
    text: 'A',
    code: ' table ',
  },
  {
    text: 'must be available on the page for DataTables to use. This examples shows an empty',
    code: ' table ',
  },
  {
    text: 'element being initialising as a DataTable with a set of data from a Javascript array. The columns in the table are dynamically created based on the',
    code: ' columns.title ',
  },
  {
    text: 'configuration option.',
  },
];

export const ServerSideProcessingSubTitle: CardSubtitle[] = [
  {
    text: "There are many ways to get your data into DataTables, and if you are working with seriously large databases, you might want to consider using the server-side options that DataTables provides. With server-side processing enabled, all paging, searching, ordering actions that DataTables performs are handed off to a server where an SQL engine (or similar) can perform these actions on the large data set (after all, that's what the database engine is designed for!). As such, each draw of the table will result in a new Ajax request being made to get the required data.",
  },
  {
    text: 'Server-side processing is enabled by setting the',
    code: ' serverSide:option ',
  },
  {
    text: 'option to',
    code: ' true ',
  },
  {
    text: 'and providing an Ajax data source through the',
    code: ' ajax:option ',
  },
  {
    text: 'option.',
  },
];

const CustomBadge: React.FC<CustomBadgeProp> = ({ position, color }) => {
  return (
    <Badge pill color={color}>
      {position}
    </Badge>
  );
};

export const HtmlColumns: TableColumn<HtmlSourced>[] = [
  {
    name: 'Name',
    selector: (row) => row.name,
    sortable: true,
  },
  {
    name: 'Position',
    selector: (row) => row.position,
    sortable: true,
  },
  {
    name: 'Salary',
    selector: (row) => row.salary,
    sortable: true,
  },
  {
    name: 'Office',
    selector: (row) => row.office,
    sortable: true,
  },
  {
    name: 'CV',
    sortable: true,

    cell: () => (
      <div className="action">
        <Link
          className="pdf"
          onClick={handlePDFClick}
          rel="/assets/images/favicon.png"
          to="/assets/pdf/sample.pdf"
          target="_blank"
        >
          <i className="icofont icofont-file-pdf"> </i>
        </Link>
      </div>
    ),
  },
  {
    name: 'Status',
    selector: (row) => row.badge,
    cell: (row) => <CustomBadge color={row.color} position={row.badge} />,
    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.email,
    sortable: true,
  },
  {
    name: 'Action',
    sortable: true,

    cell: () => (
      <ul className="action">
        <li className="edit">
          <a href={Href}>
            <i className="icon-pencil-alt" />
          </a>
        </li>
        <li className="delete">
          <a href={Href}>
            <i className="icon-trash" />
          </a>
        </li>
      </ul>
    ),
  },
];

type LessonsListTableColumnsProps = {
  onEdit: (lesson: CourseLesson) => void;
  onDelete: (lesson: CourseLessonWithDeleteFlag) => void;
  courseId: string | number;
};
export const getLessonsListTableColumns = (
  onEdit: LessonsListTableColumnsProps['onEdit'],
  onDelete: LessonsListTableColumnsProps['onDelete'],
  courseId: string | number,
  t: TFunction,
): TableColumn<CourseLessonWithDeleteFlag>[] => [
  {
    name: t('courses.lessons.lessonsTable.lesson'),
    sortable: true,
    selector: (row) => row.title,
    cell: (row) => {
      const url = `/courses/details/${courseId}/lesson/${row.id}`;
      return <Link to={url}>{row.title}</Link>;
    },
  },
  {
    name: t('courses.lessons.lessonsTable.position'),
    sortable: true,
    selector: (row) => {
      if (row.lesson_pos) {
        return row.lesson_pos;
      } else return 0;
    },
  },
  // {
  //   name: t('courses.lessons.lessonsTable.video'),
  //   cell: (row) => {
  //     return (
  //       <Link to={row.video?.link ?? '#'} target="blank">
  //         {row.video?.filename ?? 'Filename'}
  //       </Link>
  //     );
  //   },
  // },
  {
    name: t('courses.lessons.lessonsTable.action'),
    sortable: false,
    cell: (row) => (
      <ul className="action">
        <li className="edit">
          <Button color="success" outline onClick={() => onEdit(row)}>
            <i className="icon-pencil-alt" />
          </Button>
        </li>
        <li className="delete">
          <Button color="danger" outline onClick={() => onDelete(row)}>
            <i className="icon-trash" />
          </Button>
        </li>
      </ul>
    ),
  },
];

export const LessonsListTableColumns: TableColumn<CourseLesson>[] = [
  {
    name: 'Lesson',
    sortable: false,
    selector: (row) => row.title,
  },
  {
    name: 'VideoID',
    selector: (row) => {
      if (row.videoId) {
        return row.videoId;
      } else return 0;
    },
  },
  {
    name: 'Video',
    cell: (row) => (
      <Link to={row.video.link} target="blank">
        {row.video.filename}
      </Link>
    ),
  },
];
type CoursesListTableColumnsProps = {
  onEdit: (course: CourseData) => void;
  onDelete: (course: CourseData) => void;
};
export const getCoursesListTableColumns = (
  onEdit: CoursesListTableColumnsProps['onEdit'],
  onDelete: CoursesListTableColumnsProps['onDelete'],
  t: TFunction,
  pageLang: string,
): TableColumn<CourseData>[] => [
  {
    name: t('courses.coursesList.table.columnCourseTitle'),
    minWidth: '115px',
    maxWidth: '200px',
    sortable: true,
    selector: (row) => {
      return getTranslate('name', pageLang, row) ?? row.name;
    },
    grow: 1,
    wrap: true,
    cell: (row) => (
      <Link to={routes.Courses.CourseDetailsPage.replace(':id', row.id.toString())}>
        {getTranslate('name', pageLang, row) ?? row.name}
      </Link>
    ),
  },
  {
    name: t('courses.coursesList.table.columnCategories'),
    selector: (row) => row.categories.length,
    minWidth: '115px',
    maxWidth: '150px',
    grow: 1,
  },
  {
    name: t('courses.coursesList.table.columnLessons'),
    selector: (row) => {
      return row.categories?.reduce((acc: number, category: any) => {
        return acc + (category.lessons?.length || 0);
      }, 0);
    },
    sortable: true,
    minWidth: '115px',
    maxWidth: '150px',
    grow: 1,
  },
  {
    name: t('courses.coursesList.table.columnStatus'),
    selector: (row) => row.courseStateStatus ?? '',
    sortable: true,
    minWidth: '115px',
    maxWidth: '150px',
    grow: 1,
  },
  {
    name: t('courses.coursesList.table.columnAction'),
    sortable: false,
    width: '180px',
    grow: 0,
    cell: (row) => (
      <ul className="action">
        <li className="edit">
          <Button color="success" outline onClick={() => onEdit(row)}>
            <i className="icon-pencil-alt" />
          </Button>
        </li>
        <li className="delete">
          <Button color="danger" outline onClick={() => onDelete(row)}>
            <i className="icon-trash" />
          </Button>
        </li>
      </ul>
    ),
  },
];

export const CoursesListTableColumns: TableColumn<StudentCourse>[] = [
  {
    name: 'Course',

    sortable: true,
    cell: (row) => (
      <Link to={routes.Students.MyCoursesCourseDetailsPage.replace(':id', row.courseId.toString())}>
        {row.name}
      </Link>
    ),
  },
  {
    name: 'Number of lessons',
    cell: (row) => {
      return row.categories.reduce((acc, category) => acc + category.lessons.length, 0);
    },
    sortable: true,
  },
];
type UsersListTableColumnsProps = {
  onEdit: (user: UserData) => void;
  onDelete: (user: UserData) => void;
};

export const getUsersListTableColumns = (
  onEdit: UsersListTableColumnsProps['onEdit'],
  onDelete: UsersListTableColumnsProps['onDelete'],
  t: TFunction,
  role: UserRole | null,
  lang: 'en' | 'ru' | 'uk',
): TableColumn<UserData>[] => [
  {
    name: t('users.table.columnPhoto'),
    sortable: false,
    width: '80px',
    cell: (row) => {
      const hasPhoto = row.photo && typeof row.photo === 'string' && row.photo.trim() !== '';

      const src = hasPhoto
        ? `${process.env.REACT_APP_BACKEND_STATIC}/userPics/${row.photo}`
        : '/assets/images/avtar/default-user.png';

      return (
        <img
          src={src}
          alt="User photo"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      );
    },
  },
  {
    name: t('users.table.columnFirstName'),
    minWidth: '115px',
    maxWidth: '200px',
    grow: 1,
    wrap: true,
    selector: (row) => row.firstName ?? '',
    sortable: true,
  },
  {
    name: t('users.table.columnLastName'),
    minWidth: '115px',
    maxWidth: '200px',
    grow: 1,
    wrap: true,
    selector: (row) => row.lastName ?? '',
    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.email ?? '',
    sortable: true,
    grow: 2,
    wrap: true,
  },
  {
    name: t('users.table.columnRole'),
    width: '150px',
    selector: (row) => row.role ?? '',
    cell: (row) => {
      if (row.role === 'student') {
        return t('users.userRoles.student');
      } else if (row.role === 'assistant') {
        return t('users.userRoles.assistant');
      } else if (row.role === 'admin') {
        return t('users.userRoles.admin');
      }
    },
    sortable: true,
  },
  {
    name: t('users.table.columnCourses'),
    selector: (row) => row.studentCourses?.length ?? 0,
    cell: (row) => {
      const nameRowId = `courses-user-info-${row.id}`;
      const tz = 'Europe/Kyiv';

      return (
        <div className="d-flex gap-2 align-items-center">
          {row.studentCourses?.length ?? 0}
          <Button id={nameRowId} size="xs" color="primary" className="ms-auto me-0">
            <i className="fa fa-info-circle" />
          </Button>
          <UncontrolledTooltip
            target={nameRowId}
            placement="left"
            container="body"
            delay={{ show: 50, hide: 50 }}
            style={{
              minWidth: '300px',
            }}
          >
            <div className="d-flex flex-column text-start">
              {row.studentCourses && row.studentCourses.length > 0 ? (
                row.studentCourses.map((sc) => (
                  <div key={sc.courseId} className="mb-2">
                    <strong>
                      {lang === 'en'
                        ? sc.course.name
                        : sc.course?.translations?.find((t) => t.lang === lang)?.name ||
                          sc.course.name}
                    </strong>{' '}
                    <br />
                    {t('users.table.enrolledAt', {
                      date: formatWithOffset(sc.enrolledAt, tz),
                    })}{' '}
                    <br />
                  </div>
                ))
              ) : (
                <div>{t('users.table.noCourseEnrollments')}</div>
              )}
            </div>
          </UncontrolledTooltip>
        </div>
      );
    },
  },
  {
    name: t('users.table.columnDOB'),
    width: '150px',
    selector: (row) => {
      const date = row.dob ? new Date(row.dob) : null;
      return date instanceof Date && !isNaN(date.getTime()) ? date.toLocaleDateString('uk-UA') : '';
    },
  },
  {
    name: t('users.table.columnAction'),
    sortable: false,
    width: '180px',
    grow: 0,
    cell: (row) => (
      <ul className="action">
        <li className="edit">
          <Button
            color="success"
            outline
            onClick={(e) => {
              e.currentTarget.blur();
              onEdit(row);
            }}
          >
            <i className="icon-pencil-alt" />
          </Button>
        </li>
        {role && role === UserRole.ADMIN && (
          <li className="delete">
            <Button
              color="danger"
              outline
              onClick={(e) => {
                e.currentTarget.blur();
                onDelete(row);
              }}
            >
              <i className="icon-trash" />
            </Button>
          </li>
        )}
      </ul>
    ),
  },
];

export const getUsersSimpleListTableColumns = (t: TFunction): TableColumn<UserData>[] => [
  {
    name: t('users.table.columnPhoto'),
    sortable: false,
    width: '80px',
    cell: (row) => {
      const hasPhoto = row.photo && typeof row.photo === 'string' && row.photo.trim() !== '';

      const src = hasPhoto
        ? `${process.env.REACT_APP_BACKEND_STATIC}/userPics/${row.photo}`
        : '/assets/images/avtar/default-user.png';

      return (
        <img
          src={src}
          alt="User photo"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      );
    },
  },
  {
    name: t('users.table.columnFirstName'),
    minWidth: '115px',
    maxWidth: '200px',
    grow: 1,
    wrap: true,
    cell: (row) => {
      const fn = row.firstName ?? '';
      const ln = row.lastName ?? '';
      const name = `${fn} ${ln}`;
      return (
        <Link to={routes.Dashboard.UserDetailsStats.replace(':userId', row.id.toString())}>
          {name}
        </Link>
      );
    },

    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.email ?? '',
    sortable: true,
    grow: 2,
    wrap: true,
  },
];
export const getFeedbackTableColumns = (t: TFunction): TableColumn<UserFeedback>[] => [
  {
    name: t('feedback.studentName'),
    minWidth: '115px',
    maxWidth: '200px',
    grow: 1,
    wrap: true,
    selector: (row) => {
      const fn = row.user.firstName ?? '';
      const ln = row.user.lastName ?? '';
      return `${fn} ${ln}`;
    },
    sortable: true,
  },
  {
    name: 'Email',
    minWidth: '115px',
    maxWidth: '200px',
    selector: (row) => row.user.email ?? '',
    sortable: true,
    grow: 2,
    wrap: true,
  },
  {
    name: t('feedback.date'),
    minWidth: '115px',
    maxWidth: '150px',
    selector: (row) =>
      new Date(row.date).toLocaleDateString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    sortable: true,
  },
  {
    name: t('feedback.subject'),
    width: '250px',
    selector: (row) => row.subject ?? '',
    sortable: true,
  },
  {
    name: t('feedback.message'),
    width: 'auto',
    cell: (row) => {
      return <p>{row.message ?? ''}</p>;
    },
    sortable: true,
  },
];

export const getLeaderBoardTableColumns = (t: TFunction): TableColumn<LeaderBoardData>[] => [
  // {
  //   name: t('exams.leaderBoard.table.firstName'),
  //   selector: (row) => row.user.firstName ?? '',
  //   sortable: true,
  // },
  {
    name: t('exams.leaderBoard.table.studentName'),
    selector: (row) => row.user.lastName ?? '',
    cell: (row) => `${row.user.lastName} ${row.user.firstName}`,
    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.user.email ?? '',
    sortable: true,
  },
  {
    name: t('exams.leaderBoard.table.totalLessons'),
    selector: (row) => row.course.totalLessons ?? '',
    sortable: true,
  },
  {
    name: t('exams.leaderBoard.table.toExamLessons'),
    selector: (row) => row.course.scorableLessons ?? '',
    sortable: true,
  },
  {
    name: t('exams.leaderBoard.table.examinedLessons'),
    selector: (row) => row.stats.lessonsExamined,
    sortable: true,
  },
  {
    name: t('exams.leaderBoard.table.attempts'),
    selector: (row) => row.stats.attempts,
  },
  {
    name: t('exams.leaderBoard.table.totalScore'),
    selector: (row) => row.stats.totalScore ?? '',
    cell: (row) => `${row.stats.totalScore} / ${row.course.scorableLessons * MAX_SCORE}`,
    sortable: true,
  },
];

export const getAllUsersLeadersTableColumns = (
  t: TFunction,
): TableColumn<AllUsersLeaderboardOptimized>[] => [
  {
    name: t('exams.totalLeaderBoard.table.studentName'),
    selector: (row) => row.lastName ?? '',
    cell: (row) => {
      const nameRowId = `stage-exam-${row.userId}`;

      return (
        <Link
          className="w-100 d-flex justify-content-between align-items-center"
          to={'#'}
          onClick={(e) => {
            e.preventDefault();
            // if (handleUnappointedExam) handleUnappointedExam(row);
          }}
        >
          {`${row.firstName} ${row.lastName}`}
          <Button id={nameRowId} size="xs" color="primary" className="ms-auto me-0">
            <i className="fa fa-info-circle" />
          </Button>
          <UncontrolledTooltip
            target={nameRowId}
            placement="left"
            container="body"
            delay={{ show: 50, hide: 50 }}
            style={{
              minWidth: '300px',
            }}
          >
            <div className="d-flex flex-column text-start">
              <div>Email: {row.email}</div>
              <div>Telegram: {row.telegram}</div>
            </div>
          </UncontrolledTooltip>
        </Link>
      );
    },
    sortable: true,
    sortField: 'lastName',
  },
  {
    name: t('exams.totalLeaderBoard.table.passedExams'),
    selector: (row) => row.assistantExamsPassed ?? 0,
    cell: (row) => <p className="w-100 text-center">{row.assistantExamsPassed}</p>,
    sortable: false,
    center: true,
  },
  {
    name: t('exams.totalLeaderBoard.table.scoredExams'),
    selector: (row) => row.uniqueLessonsWithScore ?? 0,
    cell: (row) => {
      const scoreRowId = `scores-passed-${row.userId}`;

      return (
        <div className="w-100 d-flex justify-content-between align-items-center">
          <p className="w-100 text-center mb-0">{`${row.uniqueLessonsWithScore} / ${row.totalExamsPossible}`}</p>
          <Button id={scoreRowId} size="xs" color="primary" className="ms-auto me-0">
            <i className="fa fa-info-circle" />
          </Button>
          <UncontrolledTooltip
            target={scoreRowId}
            placement="left"
            container="body"
            delay={{ show: 50, hide: 50 }}
            style={{
              minWidth: '300px',
            }}
          >
            <div className="d-flex flex-column text-start">
              <div>
                {t('exams.totalLeaderBoard.table.scoredExams')}: {row.uniqueLessonsWithScore}
              </div>
              <div>
                {t('exams.totalLeaderBoard.table.attempts')}: {row.totalExamPassings}
              </div>
            </div>
          </UncontrolledTooltip>
        </div>
      );
    },
    center: true,
    sortable: false,
  },
  {
    name: t('exams.totalLeaderBoard.table.result'),
    selector: (row) => row.sumOfBestScores ?? 0,
    cell: (row) => (
      <p className="w-100 text-center">{`${row.sumOfBestScores} / ${(Number(row.uniqueLessonsWithScore) ?? 0) * MAX_SCORE}`}</p>
    ),
    sortable: true,
    sortField: 'sumOfBestScores',
    center: true,
  },
];

export const getDBAllUsersLeadersTableColumns = (
  t: TFunction,
  handleModalOpen: (id: number) => void,
  handleHistoryOpen?: (id: number) => void,
): TableColumn<AllDBUsersLeaderboard>[] => [
  {
    name: t('exams.totalLeaderBoard.table.studentName'),
    // (
    // <span id="leader-table-header-name">
    //   {t('exams.totalLeaderBoard.table.studentName')}
    //   <UncontrolledTooltip
    //     target="leader-table-header-name"
    //     placement="top"
    //     container="body"
    //     delay={{ show: 50, hide: 50 }}
    //     style={{
    //       minWidth: '300px',
    //     }}
    //   >
    //     <span className="d-flex flex-column text-start">User name</span>
    //   </UncontrolledTooltip>
    // </span>
    // )
    selector: (row) => row.lastName ?? '',
    cell: (row) => {
      const nameRowId = `stage-exam-${row.userId}`;

      return (
        <Link
          className="w-100 d-flex justify-content-between align-items-center"
          to={routes.Dashboard.UserDetailsStats.replace(':userId', row.userId.toString())}
        >
          {`${row.firstName} ${row.lastName}`}
          <Button id={nameRowId} size="xs" color="primary" className="ms-auto me-0">
            <i className="fa fa-info-circle" />
          </Button>
          <UncontrolledTooltip
            target={nameRowId}
            placement="left"
            container="body"
            delay={{ show: 50, hide: 50 }}
            style={{
              minWidth: '300px',
            }}
          >
            <div className="d-flex flex-column text-start">
              <div>Email: {row.email}</div>
              <div>Telegram: {row.telegram}</div>
            </div>
          </UncontrolledTooltip>
        </Link>
      );
    },
    sortable: true,
    sortField: 'lastName',
  },
  {
    // name: t('exams.totalLeaderBoard.table.watchedHrs'),
    name: (
      <span id="leader-table-header-watched-hrs">
        {t('exams.totalLeaderBoard.table.watchedHrs')}
        <UncontrolledTooltip
          target="leader-table-header-watched-hrs"
          placement="top"
          container="body"
          delay={{ show: 50, hide: 50 }}
          style={{
            minWidth: '300px',
          }}
        >
          <span className="d-flex flex-column text-start">{t('global.tooltips.watchedHrs')}</span>
        </UncontrolledTooltip>
      </span>
    ),
    selector: (row) => row.watchedTimeSec ?? 0,
    cell: (row) => (
      <p className="w-100 text-center">
        {((row.watchedTimeSec ?? 0) / 3600).toFixed(2)} /{' '}
        {((row.totalVideoLengthSec ?? 0) / 3600).toFixed(2)}
      </p>
    ),
    sortable: true,
    sortField: 'watchedTimeSec',
    center: true,
  },
  {
    // name: t('exams.totalLeaderBoard.table.passedExams'),
    name: (
      <span id="leader-table-header-passed-exams">
        {t('exams.totalLeaderBoard.table.passedExams')}
        <UncontrolledTooltip
          target="leader-table-header-passed-exams"
          placement="top"
          container="body"
          delay={{ show: 50, hide: 50 }}
          style={{
            minWidth: '300px',
          }}
        >
          <span className="d-flex flex-column text-start">{t('global.tooltips.passedExams')}</span>
        </UncontrolledTooltip>
      </span>
    ),
    selector: (row) => row.totalLessonsWithPassedExams ?? 0,
    cell: (row) => (
      <p className="w-100 text-center">
        {row.totalLessonsWithPassedExams} / {row.maxExamsQuantity}
      </p>
    ),
    sortable: true,
    sortField: 'totalLessonsWithPassedExams',
    center: true,
  },

  {
    // name: t('exams.totalLeaderBoard.filteredTable.attemptsAvg'),
    name: (
      <span id="leader-table-header-attempts-avg">
        {t('exams.totalLeaderBoard.filteredTable.attemptsAvg')}
        <UncontrolledTooltip
          target="leader-table-header-attempts-avg"
          placement="top"
          container="body"
          delay={{ show: 50, hide: 50 }}
          style={{
            minWidth: '300px',
          }}
        >
          <span className="d-flex flex-column text-start">{t('global.tooltips.attempts')}</span>
        </UncontrolledTooltip>
      </span>
    ),
    selector: (row) => row.avgAttempts ?? 0,
    cell: (row) => <p className="w-100 text-center">{row.avgAttempts}</p>,
    sortable: true,
    center: true,
    width: '120px',
  },
  {
    // name: t('exams.totalLeaderBoard.filteredTable.resultAvg'),
    name: (
      <span id="leader-table-header-result-avg">
        {t('exams.totalLeaderBoard.filteredTable.resultAvg')}
        <UncontrolledTooltip
          target="leader-table-header-result-avg"
          placement="top"
          container="body"
          delay={{ show: 50, hide: 50 }}
          style={{
            minWidth: '300px',
          }}
        >
          <span className="d-flex flex-column text-start">{t('global.tooltips.result')}</span>
        </UncontrolledTooltip>
      </span>
    ),
    selector: (row) => row.totalBestScores ?? 0,
    cell: (row) => (
      <a
        className="w-100 text-center"
        href={`/scorings/avg?email=${encodeURIComponent(row.email)}`}
        target="blank"
      >
        {row.totalBestScores} | {row.avgBestScore}
      </a>
    ),
    sortable: true,
    center: true,
  },
  {
    name: t('exams.totalLeaderBoard.filteredTable.status'),
    selector: (row) => row.statusId ?? '',
    cell: (row) => {
      return (
        <div className={`w-100 d-flex justify-content-between align-items-center pe-2 gap-2`}>
          <div className="d-flex w-100 justify-content-center align-items-center">
            <Chip
              bgColor={row.statusColor ? `${row.statusColor}80` : undefined}
              textColor={'inherit'}
              label={t(`global.selectorStatus.${row.statusName}`, row.statusName)}
              borderRadius="2px"
            />
            {/* <p
              className="w-auto text-center mb-0 me-2 px-2 rounded-1"
              style={row.statusColor ? { backgroundColor: `${row.statusColor}80` } : {}}
            >
              {t(
                `exams.totalLeaderBoard.filteredTable.statusText.${row.statusName}`,
                row.statusName,
              )}
            </p> */}
          </div>

          <Button
            color="primary"
            outline
            size="xs"
            onClick={(e) => {
              handleModalOpen(row.id);
            }}
            className="me-2"
          >
            <i className="fa fa-comment" />
          </Button>
          <Button
            color="primary"
            // className='d-flex justify-content-center align-items-center h-auto'
            outline
            size="xs"
            onClick={(e) => {
              handleHistoryOpen && handleHistoryOpen(row.id);
            }}
          >
            <i className="fa fa-history"></i>
          </Button>
        </div>
      );
    },
    sortable: true,
    center: true,
    sortField: 'status',
    width: '280px',
  },
];

export const ExamsStatsCommmentsHistoryTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<StatsStatusDetails>[] => [
  {
    name: t('exams.totalLeaderBoard.filteredTable.status'),
    selector: (row) => row.statusName ?? '',
    cell: (row) => (
      <Chip
        bgColor={row.statusColor ? `${row.statusColor}80` : '#afafaf'}
        textColor={'inherit'}
        label={t(`global.selectorStatus.${row.statusName}`, row.statusName)}
        borderRadius="2px"
      />
    ),
    sortable: true,
  },
  {
    name: t('exams.totalLeaderBoard.filteredTable.comments'),
    selector: (row) => row.statusName ?? '',
    cell: (row) => (
      <div className="d-flex flex-column">
        {row.comments.map((comment, index) => (
          <p key={index}>{comment.text}</p>
        ))}
      </div>
    ),
    sortable: false,
  },
  {
    name: t('exams.totalLeaderBoard.filteredTable.screenshots'),
    selector: (row) => row.statusName ?? '',
    cell: (row) => (
      <div className="d-flex flex-column">
        {row.comments.map((comment, index) => (
          <Gallery withCaption>
            <div className="d-flex flex-wrap gap-2 mt-3">
              {comment.files.map((p, idx) => {
                const img = new window.Image();
                const formatted = {
                  ...p,
                  w: img.naturalWidth || 1600,
                  h: img.naturalHeight || 1200,
                };
                return (
                  <figure key={`${p.url}-${idx}`} className="m-0 position-relative">
                    <Item
                      original={`${process.env.REACT_APP_BACKEND_STATIC}/exam-stats/${p.url}`}
                      width={formatted.w}
                      height={formatted.h}
                      caption={p.name}
                    >
                      {({ ref, open }: any) => (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            open();
                          }}
                        >
                          <img
                            ref={ref as React.Ref<HTMLImageElement>}
                            src={`${process.env.REACT_APP_BACKEND_STATIC}/exam-stats/${p.url}`}
                            alt={p.name ?? 'preview'}
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 6,
                              display: 'block',
                            }}
                          />
                        </a>
                      )}
                    </Item>
                  </figure>
                );
              })}
            </div>
          </Gallery>
        ))}
      </div>
    ),
    sortable: false,
  },
  {
    name: t('global.addedAt'),
    selector: (row) => formatWithOffset(row.createdAt, tz, undefined) ?? '',
    sortable: true,
  },
];
export const LeadersListTableColumns: TableColumn<LeaderBoardData>[] = [
  {
    name: 'First Name',
    selector: (row) => row.user.firstName ?? '',
    sortable: true,
  },
  {
    name: 'Last Name',
    selector: (row) => row.user.lastName ?? '',
    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.user.email ?? '',
    sortable: true,
  },
  {
    name: 'Total lessons',
    selector: (row) => row.course.totalLessons ?? '',
    sortable: true,
  },
  {
    name: 'Examined lessons',
    selector: (row) => row.stats.lessonsExamined,
    sortable: true,
  },
  {
    name: 'Attempts',
    selector: (row) => row.stats.attempts,
  },
  {
    name: 'Total score',
    selector: (row) => row.stats.totalScore ?? '',
    cell: (row) => `${row.stats.totalScore} / ${row.course.nonScoreLessons * MAX_SCORE}`,
    sortable: true,
  },
];

type ExamListTableColumnsProps = {
  handleExamListClick: (exam: ExamData) => void;
};
export const getExamListTableColumns = (
  handleExamListClick: ExamListTableColumnsProps['handleExamListClick'],
  t: TFunction,
): TableColumn<ExamData>[] => [
  {
    name: t('courses.exam.table.examTableFirstName'),
    selector: (row) => row.user.firstName ?? '',
    sortable: true,
  },
  {
    name: t('courses.exam.table.examTableLastName'),
    selector: (row) => row.user.lastName ?? '',
    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.user.email ?? '',
    sortable: false,
  },
  {
    name: t('courses.exam.table.examTableExams'),
    cell: (row) => (
      <ul className="action">
        <li className="edit">
          <Button
            color="success"
            outline
            onClick={() => {
              handleExamListClick(row);
            }}
          >
            {t('courses.exam.table.examTableExamsShowBtn')}
          </Button>
        </li>
      </ul>
    ),
  },
];

interface AuthActivity {
  date: string;
  time: string;
  tsNum: number;
  type: 'login' | 'logout';
  ts: string;
  device_id: string | null;
  session_id: string | null;
}

export const getAuthUserTableColumns = (t: TFunction): TableColumn<AuthActivity>[] => [
  {
    name: t('stats.eventDate'),
    selector: (row) => row.date ?? '',
    sortable: true,
  },
  {
    name: t('stats.eventStatTime'),
    selector: (row) => row.time ?? '',
    sortable: true,
  },
  {
    name: t('stats.eventType'),
    selector: (row) => row.type ?? '',
    cell: (row) => (row.type === 'login' ? t('stats.charts.login') : t('stats.charts.logout')),
    sortable: true,
  },
  {
    name: t('stats.charts.deviceID'),
    selector: (row) => row.device_id ?? '—',
    sortable: true,
  },
  {
    name: t('stats.charts.sessionID'),
    selector: (row) => row.session_id ?? '—',
    sortable: true,
  },
];

export interface AssistantExamData {
  answers?: string;
  answersResponseId?: string;
  course: CourseData;
  createdAt: string;
  examDate: string;
  id: number;
  lesson: CourseLesson;
  promptRequestId: string;
  propmt: string;
  questions: string;
  user: UserData;
  parentExamId: number | null;
}
type AssistantExamListTableProps = {
  handleSeeQuestions: (data: AssistantExamData) => void;
};

export const getExamsCreatedList = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<AssistantExamAttempt>[] => [
  {
    name: t('exams.examId'),
    selector: (row) => row.id, // keeps sorting usable
    cell: (row) => (
      <Link to={routes.Exams.ExamDetails.replace(':id', String(row.id))}>{`ID: ${row.id}`}</Link>
    ),
  },
  {
    name: t('exams.createdAt'),
    selector: (row) => row.createdAt, // if you want to format, do it in cell:
    cell: (row) => formatWithOffset(row.createdAt, tz, undefined),
    // new Date(row.createdAt).toLocaleString('uk-UA', {
    //   day: '2-digit',
    //   month: '2-digit',
    //   year: 'numeric',
    //   hour: '2-digit',
    //   minute: '2-digit',
    //   second: '2-digit',
    //   hour12: false,
    // }),
  },
  {
    name: t('exams.initial'),
    // Return a string for selector so sorting works alphabetically (Yes/No)
    selector: (row) => (row.attemptNo === 1 ? 'Yes' : 'No'),
    cell: (row) => <p>{row.attemptNo === 1 ? t('exams.yes') : t('exams.no')}</p>,
  },
];

type DateLike = Date | string | null | undefined;

export function getLeadsActivityTableColumns<T>(
  t: TFunction,
  tz: string = 'Europe/Kyiv',
  map: {
    task: (row: T) => string | null | undefined;
    taskType: (row: T) => TaskType;
    deadline: (row: T) => DateLike;
    updatedAt: (row: T) => DateLike;
    comment: (row: T) => string | null | undefined;
    onActionClick?: (row: T) => void;
    filesCount?: (row: T) => number;
  },
): TableColumn<T>[] {
  const fmt = (d: DateLike) => (d ? formatWithOffset(d, tz, undefined) : '—');

  const ts = (d: DateLike) => (d ? new Date(d as any).getTime() : 0);

  return [
    {
      name: t('leads.tasksTable.task'),
      selector: (row: T) => map.task(row) ?? '',
      cell: (row: T) => (
        <div className="d-flex w-100 justify-content-between align-items-center">
          <p className="m-0">{map.task(row) ?? ''}</p>
        </div>
      ),
      sortable: true,
    },
    {
      name: t('leads.tasksTable.type'),
      selector: (row: T) => map.taskType(row) ?? '',
      cell: (row: T) => (
        <div className="d-flex w-100 justify-content-between align-items-center">
          <p className="m-0">{t(`leads.table.taskType.${map.taskType(row)}`) ?? ''}</p>
        </div>
      ),
      sortable: true,
    },

    {
      name: t('leads.tasksTable.deadline'),
      selector: (row: T) => ts(map.deadline(row)),
      cell: (row: T) => fmt(map.deadline(row)),
      width: '230px',
      sortable: true,
    },
    {
      name: t('leads.tasksTable.comment'),
      selector: (row: T) => map.comment(row) ?? '',
      cell: (row: T) => {
        return (
          <div className="d-flex w-100 justify-content-start align-items-center">
            <p className="m-0">{map.comment(row) ?? ''}</p>
          </div>
        );
      },
      sortable: false,
    },
    {
      name: t('leads.tasksTable.updated'),
      width: '230px',
      selector: (row: T) => ts(map.updatedAt(row)),
      cell: (row: T) => fmt(map.updatedAt(row)),
      sortable: true,
    },
    {
      name: t('leads.tasksTable.action'),
      width: '120px',
      // use count for sorting if provided (fallback 0)
      selector: (row: T) => map.filesCount?.(row) ?? 0,
      sortable: false,
      cell: (row: T) => {
        const count = map.filesCount?.(row) ?? 0;
        const hasComment = map.comment?.(row)?.length;
        const icon = count === 0 && !hasComment ? 'fa fa-comment' : 'fa fa-folder-open-o';

        return (
          <div className="d-flex align-items-center gap-2">
            <Button outline color="primary" size="xs" onClick={() => map.onActionClick?.(row)}>
              <i className={icon} />
            </Button>
            {count > 0 && <span className="badge bg-secondary">{count}</span>}
          </div>
        );
      },
    },
  ];
}

export const getCrmExaminationsTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
  scope: 'future' | 'past' | 'unset',
  lang: string,
  handleModalOpen: (
    id: number,
    comment: string,
    detailsId: number | null,
    files: ExamFile[] | null | undefined,
    examPassed: boolean | null,
  ) => void,
  handleUnappointedExam?: (exam: AssistantExamResponse) => void,
): TableColumn<AssistantExamResponse>[] => {
  const getStatus = (r: { isAppointed: boolean; isPassed: boolean }) => {
    if (r.isAppointed && !r.isPassed) {
      return 'aptdNotPassed';
    } else if (r.isAppointed && r.isPassed) {
      return 'aptdPassed';
    } else {
      return 'notAptd';
    }
  };

  if (scope === 'unset') {
    return [
      {
        name: t('assistantExam.table.name'),
        selector: (row) => `${row.user.firstName} ${row.user.lastName}`,

        cell: (row) => {
          const nameRowId = `stage-exam-${row.id}`;

          return (
            <Link
              className="w-100 d-flex justify-content-between align-items-center"
              to={'#'}
              onClick={(e) => {
                e.preventDefault();
                if (handleUnappointedExam) handleUnappointedExam(row);
              }}
            >
              {`${row.user.firstName} ${row.user.lastName}`}
              <Button id={nameRowId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={nameRowId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '300px',
                }}
              >
                <div className="d-flex flex-column text-start">
                  <div>Email: {row.user.email}</div>
                  <div>Telegram: {row.user.telegram}</div>
                </div>
              </UncontrolledTooltip>
            </Link>
          );
        },
        sortable: true,
        sortField: 'lastName',
      },
      {
        name: t('courses.exam.table.examTableDate'),
        selector: (row) => row.examDate,
        cell: (row) => {
          const examDateRowId = `exam-date-${row.id}`;
          const courseName =
            lang === 'en'
              ? row.course.name
              : row.lesson.translations?.find((x) => x.lang === lang)?.title;
          const categoryName =
            lang === 'en'
              ? row.lesson.category.name
              : row.lesson.category.translations?.find((x) => x.lang === lang)?.name;
          const lessonName =
            lang === 'en'
              ? row.lesson.title
              : row.lesson.translations?.find((x) => x.lang === lang)?.title;

          return (
            <div className="w-100 d-flex justify-content-between align-items-center">
              <span>
                {/* {showTimeLeft && (
                  <span>
                    {row?.hoursLeft} {t('global.h')},
                  </span>
                )}{' '} */}
                {formatWithOffset(row.examDate, tz, undefined)}
              </span>
              <Button id={examDateRowId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={examDateRowId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '350px',
                }}
              >
                <div className="d-flex flex-column text-start">
                  <div>
                    {t('courses.exam.course')}: {courseName}
                  </div>
                  <div>
                    {t('courses.exam.category')}: {categoryName}
                  </div>
                  <div>
                    {t('courses.exam.lesson')}: {lessonName}
                  </div>
                </div>
              </UncontrolledTooltip>
            </div>
          );
        },
        sortable: true,
        sortField: 'date',
      },
      {
        name: t('courses.exam.table.examTableStatus'),
        selector: (row) => getStatus(row.assistant),
        cell: (row) => <span>{t(`courses.exam.history.state.${getStatus(row.assistant)}`)}</span>,
      },
    ];
  }
  if (scope === 'past') {
    return [
      {
        name: t('assistantExam.table.name'),
        sortable: true,
        selector: (row) => `${row.user.firstName} ${row.user.lastName}`,
        cell: (row) => {
          const nameRowId = `stage-exam-${row.id}`;

          return (
            <div className="w-100 d-flex justify-content-between align-items-center">
              <Link
                to={routes.Exams.ExamDetails.replace(':id', row.id.toString())}
              >{`${row.user.firstName} ${row.user.lastName}`}</Link>
              <Button id={nameRowId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={nameRowId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '300px',
                }}
              >
                <div className="d-flex flex-column text-start">
                  <div>Email: {row.user.email}</div>
                  <div>Telegram: {row.user.telegram}</div>
                </div>
              </UncontrolledTooltip>
            </div>
          );
        },
        width: '15%',
        sortField: 'lastName',
      },
      {
        name: t('courses.exam.table.examTableDate'),
        selector: (row) => row.examDate,
        cell: (row) => {
          const examDateRowId = `exam-date-${row.id}`;
          const courseName =
            lang === 'en'
              ? row.course.name
              : row.lesson.translations?.find((x) => x.lang === lang)?.title;
          const categoryName =
            lang === 'en'
              ? row.lesson.category.name
              : row.lesson.category.translations?.find((x) => x.lang === lang)?.name;
          const lessonName =
            lang === 'en'
              ? row.lesson.title
              : row.lesson.translations?.find((x) => x.lang === lang)?.title;

          return (
            <div className="w-100 d-flex justify-content-between align-items-center">
              <span>{formatWithOffset(row.examDate, tz, undefined)}</span>
              <Button id={examDateRowId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={examDateRowId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '350px',
                }}
              >
                <div className="d-flex flex-column text-start">
                  <div>
                    {t('courses.exam.course')}: {courseName}
                  </div>
                  <div>
                    {t('courses.exam.category')}: {categoryName}
                  </div>
                  <div>
                    {t('courses.exam.lesson')}: {lessonName}
                  </div>
                </div>
              </UncontrolledTooltip>
            </div>
          );
        },
        sortable: true,
        sortField: 'examDate',
        width: '20%',
      },
      {
        name: t('courses.exam.table.examTableStatus'),
        selector: (row) => getStatus(row.assistant),
        cell: (row) => <span>{t(`courses.exam.history.state.${getStatus(row.assistant)}`)}</span>,
        width: '10%',
      },
      {
        name: t('courses.exam.table.examTableTrLength'),
        selector: (row) => row.assistant.initial?.transcriptionLength ?? 0,
        cell: (row) => <span>{row.assistant.initial?.transcriptionLength ?? ' - '}</span>,
        width: '10%',
      },
      {
        name: t('courses.exam.table.examComment'),
        selector: (row) => row.details?.comment ?? row.id,
        cell: (row) => {
          const comment = row.details?.comment ?? '-';
          let key = '-';
          if (row.details?.examPassed === true) {
            key = 'yes';
          } else if (row.details?.examPassed === false) {
            key = 'no';
          } else {
            key = '-';
          }
          const examPassed = key === '-' ? key : t(`exams.${key}`, '-');
          const commentDate = row.details?.updatedAt
            ? formatWithOffset(row.details?.updatedAt, tz, undefined)
            : '-';
          return (
            <div className="d-flex flex-column">
              {row.details?.comment && (
                <p className="m-0 line-clamp-2">
                  {t('courses.exam.table.examComment')}: {comment}
                </p>
              )}
              {key !== '-' && (
                <p className="m-0 line-clamp-2">
                  {t('courses.exam.table.examPassed')}: {examPassed}
                </p>
              )}
              {commentDate !== '-' && (
                <p className="m-0 line-clamp-2">
                  {t('courses.exam.table.updatedAt')}: {commentDate}
                </p>
              )}
            </div>
          );
        },
        width: 'auto',
      },
      // {
      //   name: t('courses.exam.table.examCommentDate'),
      //   selector: (row) => row.details?.commentDate ?? row.id,
      //   cell: (row) => {
      //     const commentDate = row.details?.commentDate
      //       ? formatWithOffset(row.details?.commentDate, tz, undefined)
      //       : '-';
      //     return <div>{commentDate}</div>;
      //   },
      // },
      {
        name: t('courses.exam.table.addComment'),
        cell: (row) => {
          return (
            <div className="w-100 d-flex justify-content-center">
              <Button
                onClick={() =>
                  handleModalOpen(
                    row.id,
                    row.details?.comment ?? '',
                    row.details?.id ?? null,
                    row.details?.files,
                    row.details?.examPassed ?? null,
                  )
                }
                color="primary"
                outline
                size="xs"
              >
                <i className="fa fa-comment-o"></i>
              </Button>
              {/* {count > 0 && <span className="badge bg-secondary">{count}</span>} */}
            </div>
          );
        },
        width: '200px',
      },
    ];
  }
  return [
    {
      name: t('assistantExam.table.name'),
      sortable: true,
      selector: (row) => `${row.user.firstName} ${row.user.lastName}`,
      cell: (row) => {
        const nameRowId = `stage-exam-${row.id}`;

        return (
          <div className="w-100 d-flex justify-content-between align-items-center">
            <Link
              to={routes.Exams.ExamDetails.replace(':id', row.id.toString())}
            >{`${row.user.firstName} ${row.user.lastName}`}</Link>
            <Button id={nameRowId} size="xs" color="primary" className="ms-auto me-0">
              <i className="fa fa-info-circle" />
            </Button>
            <UncontrolledTooltip
              target={nameRowId}
              placement="left"
              container="body"
              delay={{ show: 50, hide: 50 }}
              style={{
                minWidth: '300px',
              }}
            >
              <div className="d-flex flex-column text-start">
                <div>Email: {row.user.email}</div>
                <div>Telegram: {row.user.telegram}</div>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
      width: '15%',
      sortField: 'lastName',
    },
    {
      name: t('courses.exam.table.examTableDate'),
      selector: (row) => row.examDate,
      cell: (row) => {
        const showTimeLeft = scope === 'future';
        const examDateRowId = `exam-date-${row.id}`;
        const courseName =
          lang === 'en'
            ? row.course.name
            : row.lesson.translations?.find((x) => x.lang === lang)?.title;
        const categoryName =
          lang === 'en'
            ? row.lesson.category.name
            : row.lesson.category.translations?.find((x) => x.lang === lang)?.name;
        const lessonName =
          lang === 'en'
            ? row.lesson.title
            : row.lesson.translations?.find((x) => x.lang === lang)?.title;

        return (
          <div className="w-100 d-flex justify-content-between align-items-center">
            <span>
              {showTimeLeft && (
                <span>
                  {row?.hoursLeft} {t('global.h')},
                </span>
              )}{' '}
              {formatWithOffset(row.examDate, tz, undefined)}
            </span>
            <Button id={examDateRowId} size="xs" color="primary" className="ms-auto me-0">
              <i className="fa fa-info-circle" />
            </Button>
            <UncontrolledTooltip
              target={examDateRowId}
              placement="left"
              container="body"
              delay={{ show: 50, hide: 50 }}
              style={{
                minWidth: '350px',
              }}
            >
              <div className="d-flex flex-column text-start">
                <div>
                  {t('courses.exam.course')}: {courseName}
                </div>
                <div>
                  {t('courses.exam.category')}: {categoryName}
                </div>
                <div>
                  {t('courses.exam.lesson')}: {lessonName}
                </div>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
      sortable: true,
      sortField: 'examDate',
      width: '20%',
    },
    {
      name: t('courses.exam.table.examTableStatus'),
      selector: (row) => getStatus(row.assistant),
      cell: (row) => <span>{t(`courses.exam.history.state.${getStatus(row.assistant)}`)}</span>,
      width: '10%',
    },
    {
      name: t('courses.exam.table.examComment'),
      selector: (row) => row.details?.comment ?? row.id,
      cell: (row) => {
        const comment = row.details?.comment ?? '-';
        let key = '-';
        if (row.details?.examPassed === true) {
          key = 'yes';
        } else if (row.details?.examPassed === false) {
          key = 'no';
        } else {
          key = '-';
        }
        const examPassed = key === '-' ? key : t(`exams.${key}`, '-');
        const commentDate = row.details?.commentDate
          ? formatWithOffset(row.details?.commentDate, tz, undefined)
          : '-';
        return (
          <div className="d-flex flex-column">
            {row.details?.comment && (
              <p className="m-0 line-clamp-2">
                {t('courses.exam.table.examComment')}: {comment}
              </p>
            )}
            <p className="m-0 line-clamp-2">
              {t('courses.exam.table.examPassed')}: {examPassed}
            </p>
            <p className="m-0 line-clamp-2">
              {t('courses.exam.table.updatedAt')}: {commentDate}
            </p>
          </div>
        );
      },
      width: 'auto',
    },
    // {
    //   name: t('courses.exam.table.examCommentDate'),
    //   selector: (row) => row.details?.commentDate ?? row.id,
    //   cell: (row) => {
    //     const commentDate = row.details?.commentDate
    //       ? formatWithOffset(row.details?.commentDate, tz, undefined)
    //       : '-';
    //     return <div>{commentDate}</div>;
    //   },
    // },
    {
      name: t('courses.exam.table.addComment'),
      cell: (row) => {
        return (
          <div className="w-100 d-flex justify-content-center">
            <Button
              onClick={() =>
                handleModalOpen(
                  row.id,
                  row.details?.comment ?? '',
                  row.details?.id ?? null,
                  row.details?.files,
                  row.details?.examPassed ?? null,
                )
              }
              color="primary"
              outline
              size="xs"
            >
              <i className="fa fa-comment-o"></i>
            </Button>
            {/* {count > 0 && <span className="badge bg-secondary">{count}</span>} */}
          </div>
        );
      },
      width: '200px',
    },
  ];
};

export const getExamHistoryTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
  lang: string,
  notAppointedHandler?: (data: ExamHistoryItem) => void,
  // showExamDatails: (row: ExamHistoryItem) => void,
): TableColumn<ExamHistoryItem>[] => {
  const getStatus = (r: { isAppointed: boolean; isPassed: boolean }) => {
    if (r.isAppointed && !r.isPassed) {
      return 'aptdNotPassed';
    } else if (r.isAppointed && r.isPassed) {
      return 'aptdPassed';
    } else {
      return 'notAptd';
    }
  };

  return [
    {
      name: t('courses.exam.table.examTableFirstName'),
      selector: (row) => row.user.lastName,
      cell: (row) => (
        <div className="d-flex w-100 justify-content-between">
          {row.assistant.rootId && (
            <Link
              to={routes.Exams.ExamDetails.replace(':id', row.assistant.rootId.toString())}
            >{`${row.user.firstName} ${row.user.lastName}`}</Link>
          )}
          {!row.assistant.rootId && notAppointedHandler && (
            <Link
              to={'#'}
              onClick={(e) => {
                e.preventDefault();
                notAppointedHandler(row);
              }}
            >{`${row.user.firstName} ${row.user.lastName}`}</Link>
          )}
        </div>
      ),
      sortable: true,
      sortField: 'user',
    },
    {
      name: t('courses.exam.table.examTableStatus'),
      selector: (row) => getStatus(row.assistant),
      cell: (row) => <span>{t(`courses.exam.history.state.${getStatus(row.assistant)}`)}</span>,
    },
    {
      name: 'Email',
      selector: (row) => row.user.email,
      sortable: false,
    },
    {
      name: t('courses.exam.table.examTableLesson'),
      selector: (row) => row.lesson.title,
      cell: (row) => {
        if (lang === 'en') {
          return <span>{row.lesson.title}</span>;
        } else if (lang === 'uk' || lang === 'ru') {
          const tr = (lang: string) => row.lesson.translations?.find((x) => x.lang === lang);
          return <span>{tr(lang)?.title}</span>;
        }
      },
      sortable: false,
      sortField: 'lessonTitle',
    },
    {
      name: t('courses.exam.table.examTableDate'),
      selector: (row) => row.date,
      cell: (row) => formatWithOffset(row.date, tz, undefined),
      sortable: true,
      sortField: 'examDate',
    },
  ];
};

export const getExamScoringColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
  handleScoringDetailsModalWindow: (row: ScoringData, key: 'tr' | 'q' | 'a' | 'p') => void,
): TableColumn<ScoringData>[] => [
  {
    name: t('scoring.resultsTable.attempt'),
    selector: (row) => row.scoringAttempt,
    width: '130px',
    sortable: true,
  },
  // {
  //   name: t('scoring.resultsTable.name'),
  //   selector: (row) => row.firstName,
  //   cell: (row) => <span>{`${row.firstName} ${row.lastName}`}</span>,
  //   sortable: false,
  // },
  // {
  //   name: t('scoring.resultsTable.examId'),
  //   selector: (row) => row.examId,
  //   sortable: false,
  // },
  {
    name: t('scoring.resultsTable.incomeData'),
    selector: (row) => row.id,
    cell: (row) => (
      <div className="w-100 d-flex gap-2 justify-content-between pe-2">
        <Link
          className=""
          to={'#'}
          onClick={(e) => {
            e.preventDefault();
            if (handleScoringDetailsModalWindow) handleScoringDetailsModalWindow(row, 'tr');
          }}
        >
          {t('scoring.resultsTable.transcription')}
        </Link>
        <Link
          className=" "
          to={'#'}
          onClick={(e) => {
            e.preventDefault();
            if (handleScoringDetailsModalWindow) handleScoringDetailsModalWindow(row, 'q');
          }}
        >
          {t('scoring.resultsTable.questions')}
        </Link>
        <Link
          className=""
          to={'#'}
          onClick={(e) => {
            e.preventDefault();
            if (handleScoringDetailsModalWindow) handleScoringDetailsModalWindow(row, 'p');
          }}
        >
          {t('scoring.resultsTable.prompt')}
        </Link>
      </div>
    ),
    width: 'auto',
    minWidth: '50%',
    sortable: false,
  },
  // {
  //   name: t('scoring.resultsTable.questions'),
  //   selector: (row) => row.rawResponse,
  //   cell: (row) => (
  //     <Link
  //       className="w-100 d-flex justify-content-between align-items-center"
  //       to={'#'}
  //       onClick={(e) => {
  //         e.preventDefault();
  //         if (handleScoringDetailsModalWindow) handleScoringDetailsModalWindow(row, 'q');
  //       }}
  //     >
  //       {t('scoring.resultsTable.questions')}
  //     </Link>
  //   ),
  //   sortable: false,
  // },
  // {
  //   name: t('scoring.resultsTable.prompt'),
  //   selector: (row) => row.scoringPrompt,
  //   cell: (row) => (
  //     <Link
  //       className="w-100 d-flex justify-content-between align-items-center"
  //       to={'#'}
  //       onClick={(e) => {
  //         e.preventDefault();
  //         if (handleScoringDetailsModalWindow) handleScoringDetailsModalWindow(row, 'p');
  //       }}
  //     >
  //       {t('scoring.resultsTable.prompt')}
  //     </Link>
  //   ),
  //   sortable: false,
  // },
  {
    name: t('scoring.resultsTable.result'),
    selector: (row) => row.rawResponse,
    cell: (row) => (
      <Link
        className="w-100 d-flex justify-content-between align-items-center"
        to={'#'}
        onClick={(e) => {
          e.preventDefault();
          if (handleScoringDetailsModalWindow) handleScoringDetailsModalWindow(row, 'a');
        }}
      >
        {t('scoring.resultsTable.answers')}
      </Link>
    ),
    sortable: false,
    width: '250px',
  },
  {
    name: t('scoring.resultsTable.score'),
    selector: (row) => row.score,
    cell: (row) => row.score,
    sortable: true,
    width: '150px',
  },
];

export const getAssistantExamListTableColumns = (
  handleSeeQuestions: AssistantExamListTableProps['handleSeeQuestions'],
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<AssistantExamData>[] => [
  {
    name: t('assistantExam.table.name'),
    sortable: true,
    selector: (row) => `${row.user.firstName} ${row.user.lastName}`,
    cell: (row) => (
      <Link
        to={routes.Exams.ExamDetails.replace(':id', row.id.toString())}
      >{`${row.user.firstName} ${row.user.lastName}`}</Link>
    ),
  },
  {
    name: t('assistantExam.table.lesson'),
    selector: (row) => row.lesson.title,
  },
  {
    name: t('assistantExam.table.course'),
    selector: (row) => row.course.name,
  },
  {
    id: 'examDate',
    name: t('assistantExam.table.examDate'),
    cell: (row) => formatWithOffset(row.examDate, tz, undefined),

    selector: (row) => row.examDate,
    sortable: true,
  },
  {
    name: t('assistantExam.table.questions'),
    cell: (row) => {
      return (
        <Button onClick={() => handleSeeQuestions(row)}>
          {t('assistantExam.table.questionsButtonTxt')}
        </Button>
      );
    },
  },
];
export const ExamListTableColumns: TableColumn<ExamData>[] = [
  {
    name: 'First Name',
    selector: (row) => row.user.firstName ?? '',
    sortable: true,
  },
  {
    name: 'Last Name',
    selector: (row) => row.user.lastName ?? '',
    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.user.email ?? '',
    sortable: false,
  },
  // {
  //   name: "Topic",
  //   selector: (row) => row.examTopic,
  //   sortable: true,
  // },
];

export const ExamRequestsTableColumns: TableColumn<ExamRequest>[] = [
  {
    name: 'First Name',
    selector: (row) => row.first_name ?? '',
    sortable: true,
  },
  {
    name: 'Last Name',
    selector: (row) => row.last_name ?? '',
    sortable: true,
  },
  {
    name: 'Email',
    selector: (row) => row.email ?? '',
    sortable: false,
  },
  {
    name: 'Topic',
    selector: (row) => row.examTopic,
    sortable: true,
  },
  {
    name: 'Date',
    selector: (row) => row.date,
    sortable: true,
  },
];

export const VideoListTableColumns: TableColumn<VideoData>[] = [
  {
    name: 'Video Name',
    sortable: true,
    cell: (row) => (
      <a href={row.link} target="_blank" rel="noopener noreferrer">
        {row.filename ?? 'Unnamed Video'}
      </a>
    ),
  },

  {
    name: 'OpenAi Thread ID',
    selector: (row) => row.threadId ?? '',
    sortable: false,
  },
];

export const getVideoListTableColumns = (t: TFunction): TableColumn<VideoData>[] => {
  return [
    {
      name: t('video.videoName'),
      sortable: true,
      cell: (row) => (
        <a href={row.videoUrl ? row.videoUrl : row.link} target="_blank" rel="noopener noreferrer">
          {row.filename ?? 'Unnamed Video'}
        </a>
      ),
    },

    {
      name: 'OpenAi Thread ID',
      selector: (row) => row.threadId ?? '',
      sortable: false,
    },
  ];
};

export const getBotFilesTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<BotFilesData>[] => {
  return [
    {
      name: t('bot.botTableColumns.fileName'),
      sortable: true,
      cell: (row) => (
        <a
          href={`${process.env.REACT_APP_BACKEND_STATIC}${row.url}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.fileName ?? 'Unnamed file'}
        </a>
      ),
    },

    {
      name: t('bot.botTableColumns.date'),
      selector: (row) => new Date(row.createdAt).getTime(), // <-- numeric value for sorting
      sortable: true,
      cell: (row) => formatWithOffset(row.createdAt, tz, undefined),
    },
    {
      name: t('global.edit'),
      cell: (row) => {
        const fileId = row.fileName.split('.').slice(0, -1).join('.');
        return (
          <ul className="action">
            <li className="edit">
              <a href={'bot/' + fileId}>
                <i className="icon-pencil-alt" />
              </a>
            </li>
          </ul>
        );
      },
    },
  ];
};
type Row = LeadsTableData;
export const getLeadsTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<Row>[] => {
  const hasComment = (c: unknown): boolean => typeof c === 'string' && c.trim().length > 0;

  const isWaiting = (d: Row['deadlines'][number]) => !hasComment(d.comment);

  const nearestItem = (row: Row) => {
    const waiting = (row.deadlines ?? []).filter(isWaiting);
    if (waiting.length === 0) return null;

    let best = waiting[0];
    let min = Date.parse(best.deadline as unknown as string);
    for (let i = 1; i < waiting.length; i++) {
      const ts = Date.parse(waiting[i].deadline as unknown as string);
      if (Number.isFinite(ts) && ts < min) {
        min = ts;
        best = waiting[i];
      }
    }
    return best;
  };

  const nearestTs = (row: Row) => {
    const waiting = (row.deadlines ?? []).filter(isWaiting);
    if (!waiting.length) return Number.MAX_SAFE_INTEGER; // в кінець таблиці
    return Math.min(
      ...waiting
        .map((d) => Date.parse(d.deadline as unknown as string))
        .filter((ts) => Number.isFinite(ts)),
    );
  };

  return [
    {
      name: t('leads.table.name'),
      selector: (row) => row.fullName,
      cell: (row) => <a href={`leadslist/details/${row.id}`}>{row.fullName}</a>,
      sortable: true,
      sortField: 'fullName',
    },
    {
      name: t('leads.table.contacts'),
      selector: (row) => row.email,
      cell: (row) => (
        <div className="d-flex flex-column gap-2">
          <span>{row.email}</span>
          <span>{row.phone}</span>
        </div>
      ),
      sortable: false,
    },
    // {
    //   name: t('leads.table.dateToDb'),
    //   selector: (row) => new Date(row.createdAt).getTime(),
    //   cell: (row) => formatWithOffset(row.createdAt, tz, undefined),
    //   sortable: true,
    //   sortField: 'createdAt',
    // },
    {
      name: t('leads.table.stage'),
      selector: (row) => row.id ?? '',
      cell: (row) => {
        const stageRowId = `stage-info-${row.id}`;
        const active = row.flows.find((el) => el.stageStatus === LeadStageStatus.ACTIVE);
        const actionsNumber = row.flows.reduce((acc, curr) => {
          return acc + curr.dataRows.length;
        }, 0);
        const advDate = formatWithOffset(row.advertDate, tz, undefined);
        return (
          <div className="w-100 pe-3 d-flex justify-content-between">
            {' '}
            {active ? (
              <div className="d-flex gap-1">
                <span>
                  {t('leads.table.phase', {
                    num: active?.position,
                  })}{' '}
                </span>
                <span>
                  {t('leads.table.actionsNum', {
                    num: actionsNumber,
                  })}{' '}
                </span>
              </div>
            ) : (
              <span>{t('leads.table.phaseAllClosed')}</span>
            )}
            <Button id={stageRowId} size="xs" color="primary" className="ms-auto me-0">
              <i className="fa fa-info-circle" />
            </Button>
            <UncontrolledTooltip
              target={stageRowId}
              placement="left"
              container="body"
              delay={{ show: 50, hide: 50 }}
              style={{
                minWidth: '300px',
              }}
            >
              <div className="d-flex flex-column text-start">
                <div>
                  {t('leads.table.dateReaction')} : {advDate}
                </div>
                <div>
                  <div className="mt-1">{t('leads.table.stageCloseDates')}:</div>
                  {row.flows.map((f) => (
                    <div>
                      {t('leads.table.phase', { num: f.position })} -{' '}
                      {f.stageStatus === LeadStageStatus.CLOSED
                        ? formatWithOffset(f.updatedAt, tz, undefined)
                        : t('leads.table.notClosed')}
                    </div>
                  ))}
                </div>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
      sortable: false,
    },
    {
      name: t('leads.table.nearestDeadline'),
      selector: nearestTs, // numeric timestamp for sorting
      sortField: 'nextDeadline',
      sortable: true,
      width: '360px',
      cell: (row) => {
        const tz = 'Europe/Kyiv'; // або ваша змінна
        const infoId = `deadlines-info-${row.id}`;
        const overdueId = `overdues-info-${row.id}`;

        const hasComment = (c: unknown) => typeof c === 'string' && c.trim().length > 0;

        // 1) Чекаючі (без коментаря)
        const waiting = (row.deadlines ?? []).filter((d) => !hasComment(d.comment));

        // Найближча серед “чекаючих”
        const n =
          waiting.length > 0
            ? [...waiting].sort(
                (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
              )[0]
            : null;

        // 2) Просрочки: без комента й дедлайн у минулому АБО комент після дедлайну
        const nowMs = Date.now();
        const toMs = (v: string | Date | null | undefined) =>
          v ? new Date(v as any).getTime() : NaN;
        const round2 = (x: number) => Math.round(x * 100) / 100;

        const leadOverdues = (row.deadlines ?? [])
          .filter((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const deadlinePassed = Number.isFinite(dlMs) && nowMs > dlMs;
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            return (deadlinePassed && !_hasComment) || overdueComment;
          })
          .map((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            const hoursLate = overdueComment
              ? round2((updMs - dlMs) / 3_600_000)
              : round2((nowMs - dlMs) / 3_600_000);
            return { ...d, whenDone: _hasComment ? d.updatedAt : null, hoursLate };
          })
          .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
        return (
          <div className="w-100">
            <div className="w-100 d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span>
                  {waiting.length === 0
                    ? '—'
                    : waiting.length > 1
                      ? `${waiting.length} шт.`
                      : `${n!.remaining} ${t('global.h')} ${formatWithOffset(n!.deadline, tz, undefined)}`}
                </span>
              </div>

              {/* info button with a tooltip listing upcoming deadlines */}
              <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={infoId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '600px',
                }}
              >
                <div className="text-start">
                  <div className="fw-semibold mb-1">{t('leads.table.upcomingTasks', 'Задачі')}</div>
                  {waiting?.length ? (
                    <ul className="mb-0 ps-3">
                      {waiting
                        .slice() // don’t mutate
                        .sort(
                          (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
                        )
                        .map((d, i) => (
                          <li key={`${row.id}-${i}`} className="d-block p-0">
                            {i + 1}
                            {')'} {d.task}: {d.remaining} {t('global.h')} {t('global.untill')}{' '}
                            {formatWithOffset(d.deadline, tz, undefined)}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <span>{t('leads.noDeadlines', 'No pending tasks')}</span>
                  )}
                </div>
              </UncontrolledTooltip>
            </div>
            {/* Line 2: Просрочки — X шт. (i) (ONLY if there are overdues) */}
            {leadOverdues.length > 0 && (
              <div className="w-100 d-flex justify-content-between align-items-center">
                <div className="small text-danger">
                  {t('leads.table.overduesLabel')} — {leadOverdues.length}{' '}
                  {t('global.qtyShort', 'шт.')}{' '}
                  <Button id={overdueId} size="xs" color="danger" className="ms-1 py-0 px-1">
                    <i className="fa fa-info-circle" />
                  </Button>
                </div>

                <UncontrolledTooltip
                  target={overdueId}
                  placement="left"
                  container="body"
                  delay={{ show: 50, hide: 50 }}
                  autohide={false}
                  style={{ minWidth: '720px' }}
                >
                  <div className="text-start p-2">
                    <div className="fw-semibold mb-2">{t('leads.table.overdueTasks')}</div>
                    <div
                      className="table-responsive tooltip-table"
                      style={{ maxHeight: 320, overflowY: 'auto' }}
                    >
                      <table className="table table-sm table-striped mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="px-1" style={{ width: 20 }}>
                              #
                            </th>

                            <th className="px-1" style={{ minWidth: 180 }}>
                              {t('leads.table.action')}
                            </th>
                            <th className="px-1">{t('leads.table.taskStage')}</th>

                            <th className="px-1">{t('leads.table.taskTypeTitle')}</th>

                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.deadline')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.doneAt')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.hoursLate')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadOverdues.map((d, idx) => (
                            <tr key={`${row.id}-ov-${idx}`}>
                              <td className="px-1">{idx + 1}</td>

                              <td className="px-1" style={{ maxWidth: 260 }} title={d.task}>
                                {d.task}
                              </td>
                              <td className="px-1" style={{ maxWidth: 260 }} title={d.stage}>
                                {t(`leads.table.${d.stage}`)}
                              </td>
                              <td className="px-1" style={{ maxWidth: 260 }} title={d.taskType}>
                                {t(`leads.table.taskType.${d.taskType}`)}
                              </td>
                              <td className="px-1">
                                {formatWithOffset(d.deadline, tz, undefined)}
                              </td>
                              <td className="px-1">
                                {d.whenDone ? (
                                  formatWithOffset(d.whenDone, tz, undefined)
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="px-1 text-danger">{d.hoursLate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];
};
export const getLeadsTableColumns2 = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<Row>[] => {
  const hasComment = (c: unknown): boolean => typeof c === 'string' && c.trim().length > 0;

  const isWaiting = (d: Row['deadlines'][number]) => !hasComment(d.comment);

  const nearestItem = (row: Row) => {
    const waiting = (row.deadlines ?? []).filter(isWaiting);
    if (waiting.length === 0) return null;

    let best = waiting[0];
    let min = Date.parse(best.deadline as unknown as string);
    for (let i = 1; i < waiting.length; i++) {
      const ts = Date.parse(waiting[i].deadline as unknown as string);
      if (Number.isFinite(ts) && ts < min) {
        min = ts;
        best = waiting[i];
      }
    }
    return best;
  };

  const nearestTs = (row: Row) => {
    const waiting = (row.deadlines ?? []).filter(isWaiting);
    if (!waiting.length) return Number.MAX_SAFE_INTEGER; // в кінець таблиці
    return Math.min(
      ...waiting
        .map((d) => Date.parse(d.deadline as unknown as string))
        .filter((ts) => Number.isFinite(ts)),
    );
  };

  return [
    {
      name: t('leads.table.name'),
      selector: (row) => row.fullName,
      cell: (row) => <a href={`leadslist/details/${row.id}`}>{row.fullName}</a>,
      sortable: true,
      sortField: 'fullName',
    },
    {
      name: t('leads.table.contacts'),
      selector: (row) => row.email,
      cell: (row) => (
        <div className="d-flex flex-column gap-2">
          <span>{row.email}</span>
          <span>{row.phone}</span>
        </div>
      ),
      sortable: false,
    },
    // {
    //   name: t('leads.table.dateToDb'),
    //   selector: (row) => new Date(row.createdAt).getTime(),
    //   cell: (row) => formatWithOffset(row.createdAt, tz, undefined),
    //   sortable: true,
    //   sortField: 'createdAt',
    // },
    {
      name: t('leads.table.stage'),
      selector: (row) => row.id ?? '',
      cell: (row) => {
        const stageRowId = `stage-info-${row.id}`;
        const active = row.flows.find((el) => el.stageStatus === LeadStageStatus.ACTIVE);
        const actionsNumber = row.flows.reduce((acc, curr) => {
          return acc + curr.dataRows.length;
        }, 0);
        const advDate = formatWithOffset(row.advertDate, tz, undefined);
        return (
          <div className="w-100 pe-3 d-flex justify-content-between">
            {' '}
            {active ? (
              <div className="d-flex gap-1">
                <span>
                  {t('leads.table.phase', {
                    num: active?.position,
                  })}{' '}
                </span>
                <span>
                  {t('leads.table.actionsNum', {
                    num: actionsNumber,
                  })}{' '}
                </span>
              </div>
            ) : (
              <span>{t('leads.table.phaseAllClosed')}</span>
            )}
            <Button id={stageRowId} size="xs" color="primary" className="ms-auto me-0">
              <i className="fa fa-info-circle" />
            </Button>
            <UncontrolledTooltip
              target={stageRowId}
              placement="left"
              container="body"
              delay={{ show: 50, hide: 50 }}
              style={{
                minWidth: '300px',
              }}
            >
              <div className="d-flex flex-column text-start">
                <div>
                  {t('leads.table.dateReaction')} : {advDate}
                </div>
                <div>
                  <div className="mt-1">{t('leads.table.stageCloseDates')}:</div>
                  {row.flows.map((f) => (
                    <div>
                      {t('leads.table.phase', { num: f.position })} -{' '}
                      {f.stageStatus === LeadStageStatus.CLOSED
                        ? formatWithOffset(f.updatedAt, tz, undefined)
                        : t('leads.table.notClosed')}
                    </div>
                  ))}
                </div>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
      sortable: false,
    },
    {
      name: t('leads.table.nearestDeadline'),
      selector: nearestTs, // numeric timestamp for sorting
      sortField: 'nextDeadline',
      sortable: true,
      width: '360px',
      cell: (row) => {
        const tz = 'Europe/Kyiv'; // або ваша змінна
        const infoId = `deadlines-info-${row.id}`;
        const overdueId = `overdues-info-${row.id}`;
        const nowMs = Date.now();
        const hasComment = (c: unknown) => typeof c === 'string' && c.trim().length > 0;
        const hasFiles = (f: unknown) => Array.isArray(f) && f.length > 0;
        const toMs = (v: string | Date | null | undefined) =>
          v ? new Date(v as any).getTime() : NaN;
        // 1) Чекаючі (без коментаря)
        const waiting = (row.deadlines ?? []).filter((d) => {
          return !hasComment(d.comment) && !hasFiles(d.files);
        });
        const waitingOverdue = row.control
          .filter((d) => {
            return d.reason === 'no_comment_or_files';
          })
          .sort(
            (a, b) =>
              new Date(a.leadFlowDataDeadline).getTime() -
              new Date(b.leadFlowDataDeadline).getTime(),
          );
        const waitingFuture = row.deadlines
          .filter((d) => {
            const dlMs = toMs(d.deadline);
            return Number.isFinite(dlMs) && dlMs > nowMs && !hasComment(d.comment);
          })
          .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
        // Найближча серед “чекаючих”
        const allWaiting = [...waitingOverdue, ...waitingFuture];
        console.log('waiting', waiting);
        console.log('allWaiting', allWaiting);
        const n =
          waiting.length > 0
            ? [...waiting].sort(
                (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
              )[0]
            : null;

        // 2) Просрочки: без комента й дедлайн у минулому АБО комент після дедлайну

        const round2 = (x: number) => Math.round(x * 100) / 100;

        const leadOverdues = [...(row.control ?? [])].sort(
          (a, b) =>
            new Date(a.leadFlowDataDeadline).getTime() - new Date(b.leadFlowDataDeadline).getTime(),
        );

        const countOverdueToNow = (deadline: string | Date | null | undefined) => {
          const dlMs = toMs(deadline);
          const overdue = nowMs - dlMs;
          const overdueHours = overdue / 3_600_000;
          return Number.isFinite(dlMs) && overdueHours > 0 ? `-${round2(overdueHours)}` : null;
        };

        const ifUpdatedAfterCreated = (
          deadline: string | Date | null | undefined,
          updatedAt: string | Date | null | undefined,
        ) => {
          const dlMs = toMs(deadline);
          const updMs = toMs(updatedAt);
          return Number.isFinite(updMs) && updMs > dlMs;
        };

        return (
          <div className="w-100">
            <div className="w-100 d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span>
                  {waiting.length === 0
                    ? '—'
                    : waiting.length > 1
                      ? `${waiting.length} шт.`
                      : `${n!.remaining} ${t('global.h')} ${formatWithOffset(n!.deadline, tz, undefined)}`}
                </span>
              </div>

              {/* info button with a tooltip listing upcoming deadlines */}
              <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={infoId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '600px',
                }}
              >
                <div className="text-start">
                  <div className="fw-semibold mb-1">{t('leads.table.upcomingTasks', 'Задачі')}</div>
                  {waiting?.length ? (
                    <ul className="mb-0 ps-3">
                      {waiting
                        .slice() // don’t mutate
                        .sort(
                          (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
                        )
                        .map((d, i) => (
                          <li key={`${row.id}-${i}`} className="d-block p-0">
                            {i + 1}
                            {')'} {d.task}: {d.remaining} {t('global.h')} {t('global.untill')}{' '}
                            {formatWithOffset(d.deadline, tz, undefined)}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <span>{t('leads.noDeadlines', 'No pending tasks')}</span>
                  )}
                </div>
              </UncontrolledTooltip>
            </div>
            {/* Line 2: Просрочки — X шт. (i) (ONLY if there are overdues) */}
            {leadOverdues.length > 0 && (
              <div className="w-100 d-flex justify-content-between align-items-center">
                <div className="small text-danger">
                  {t('leads.table.overduesLabel')} — {leadOverdues.length}{' '}
                  {t('global.qtyShort', 'шт.')}{' '}
                  <Button id={overdueId} size="xs" color="danger" className="ms-1 py-0 px-1">
                    <i className="fa fa-info-circle" />
                  </Button>
                </div>

                <UncontrolledTooltip
                  target={overdueId}
                  placement="left"
                  container="body"
                  delay={{ show: 50, hide: 50 }}
                  autohide={false}
                  style={{ minWidth: '720px' }}
                >
                  <div className="text-start p-2">
                    <div className="fw-semibold mb-2">{t('leads.table.overdueTasks')}</div>
                    <div
                      className="table-responsive tooltip-table"
                      style={{ maxHeight: 320, overflowY: 'auto' }}
                    >
                      <table className="table table-sm table-striped mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="px-1" style={{ width: 20 }}>
                              #
                            </th>

                            <th className="px-1" style={{ minWidth: 180 }}>
                              {t('leads.table.action')}
                            </th>
                            <th className="px-1">{t('leads.table.taskStage')}</th>

                            <th className="px-1">{t('leads.table.taskTypeTitle')}</th>

                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.deadline')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.doneAt')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.hoursLate')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadOverdues.map((d, idx) => (
                            <tr key={`${row.id}-ov-${idx}`}>
                              <td className="px-1">{idx + 1}</td>

                              <td
                                className="px-1"
                                style={{ maxWidth: 260 }}
                                title={d.leadFlowData?.task ?? 'Unknown Task'}
                              >
                                {d.leadFlowData?.task ?? 'Unknown Task'}
                              </td>
                              <td
                                className="px-1"
                                style={{ maxWidth: 260 }}
                                title={d.leadFlowData?.flow.stage ?? 'Unknown Stage'}
                              >
                                {t(`leads.table.${d.leadFlowData?.flow.stage ?? 'Unknown Stage'}`)}
                              </td>
                              <td
                                className="px-1"
                                style={{ maxWidth: 260 }}
                                title={d.leadFlowData?.taskType}
                              >
                                {t(`leads.table.taskType.${d.leadFlowData?.taskType}`)}
                              </td>
                              <td className="px-1">
                                {formatWithOffset(d.leadFlowDataDeadline, tz, undefined)}
                              </td>
                              <td className="px-1">
                                {ifUpdatedAfterCreated(
                                  d.leadFlowDataDeadline,
                                  d.leadFlowDataUpdatedAt,
                                ) ? (
                                  formatWithOffset(d.leadFlowDataUpdatedAt, tz, undefined)
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="px-1 text-danger">
                                {d.overdueHours
                                  ? d.overdueHours
                                  : countOverdueToNow(d.leadFlowDataDeadline)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];
};
export const getEngagedTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<EngagementsTableData>[] => {
  const hasComment = (c: unknown): boolean => typeof c === 'string' && c.trim().length > 0;

  const isWaiting = (d: EngagementsTableData['flowData'][number]) => !hasComment(d.comment);

  const nearestItem = (row: EngagementsTableData) => {
    const waiting = (row.flowData ?? []).filter(isWaiting);
    if (waiting.length === 0) return null;

    let best = waiting[0];
    let min = Date.parse(best.deadline as unknown as string);
    for (let i = 1; i < waiting.length; i++) {
      const ts = Date.parse(waiting[i].deadline as unknown as string);
      if (Number.isFinite(ts) && ts < min) {
        min = ts;
        best = waiting[i];
      }
    }
    return best;
  };

  const nearestTs = (row: EngagementsTableData) => {
    const waiting = (row.flowData ?? []).filter(isWaiting);
    if (!waiting.length) return Number.MAX_SAFE_INTEGER; // в кінець таблиці
    return Math.min(
      ...waiting
        .map((d) => Date.parse(d.deadline as unknown as string))
        .filter((ts) => Number.isFinite(ts)),
    );
  };

  return [
    {
      name: t('leads.table.name'),
      selector: (row) => row.fullName,
      cell: (row) => {
        const infoId = `engagenemts-info-${row.id}`;
        return (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <a href={`/leadslist/engagement/${row.id}`}>{row.fullName}</a>
            <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
              <i className="fa fa-info-circle" />
            </Button>
            <UncontrolledTooltip
              target={infoId}
              placement="left"
              container="body"
              delay={{ show: 50, hide: 50 }}
              style={{
                minWidth: '600px',
              }}
            >
              <div className="text-start d-flex flex-column">
                <span>Email: {row.email}</span>
                <span>Telegram: {row.telegram}</span>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
      sortable: true,
      sortField: 'fullname',
    },
    {
      name: t('engagements.table.watchDate'),
      selector: (row) => new Date(row.lastWatchedAt).getTime(),
      cell: (row) => formatWithOffset(row.lastWatchedAt, tz, undefined),
      sortable: true,
      sortField: 'watchDate',
    },
    {
      name: t('leads.table.nearestDeadline'),
      selector: nearestTs, // numeric timestamp for sorting
      sortField: 'nextDeadline',
      sortable: true,
      width: '360px',
      cell: (row) => {
        const tz = 'Europe/Kyiv'; // або ваша змінна
        const infoId = `deadlines-info-${row.id}`;
        const overdueId = `overdues-info-${row.id}`;

        const hasComment = (c: unknown) => typeof c === 'string' && c.trim().length > 0;

        // 1) Чекаючі (без коментаря)
        const waiting = (row.flowData ?? []).filter((d) => !hasComment(d.comment));

        // Найближча серед “чекаючих”
        const n =
          waiting.length > 0
            ? [...waiting].sort(
                (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
              )[0]
            : null;

        // 2) Просрочки: без комента й дедлайн у минулому АБО комент після дедлайну
        const nowMs = Date.now();
        const toMs = (v: string | Date | null | undefined) =>
          v ? new Date(v as any).getTime() : NaN;
        const round2 = (x: number) => Math.round(x * 100) / 100;
        const leadOverdues = (row.flowData ?? [])
          .filter((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const deadlinePassed = Number.isFinite(dlMs) && nowMs > dlMs;
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            return (deadlinePassed && !_hasComment) || overdueComment;
          })
          .map((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            const hoursLate = overdueComment
              ? round2((updMs - dlMs) / 3_600_000)
              : round2((nowMs - dlMs) / 3_600_000);
            return { ...d, whenDone: _hasComment ? d.updatedAt : null, hoursLate };
          })
          .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
        return (
          <div className="w-100">
            <div className="w-100 d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span>
                  {waiting.length === 0
                    ? '—'
                    : waiting.length > 1
                      ? `${waiting.length} шт.`
                      : `${n!.remaining} ${t('global.h')} ${formatWithOffset(n!.deadline, tz, undefined)}`}
                </span>
              </div>

              {/* info button with a tooltip listing upcoming deadlines */}
              <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={infoId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '600px',
                }}
              >
                <div className="text-start">
                  <div className="fw-semibold mb-1">{t('leads.table.upcomingTasks', 'Задачі')}</div>
                  {waiting?.length ? (
                    <ul className="mb-0 ps-3">
                      {waiting
                        .slice() // don’t mutate
                        .sort(
                          (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
                        )
                        .map((d, i) => (
                          <li key={`${row.id}-${i}`} className="d-block p-0">
                            {i + 1}
                            {')'} {d.task}: {d.remaining} {t('global.h')} {t('global.untill')}{' '}
                            {formatWithOffset(d.deadline, tz, undefined)}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <span>{t('leads.noDeadlines', 'No pending tasks')}</span>
                  )}
                </div>
              </UncontrolledTooltip>
            </div>
            {/* Line 2: Просрочки — X шт. (i) (ONLY if there are overdues) */}
            {leadOverdues.length > 0 && (
              <div className="w-100 d-flex justify-content-between align-items-center">
                <div className="small text-danger">
                  {t('leads.table.overduesLabel')} — {leadOverdues.length}{' '}
                  {t('global.qtyShort', 'шт.')}{' '}
                  <Button id={overdueId} size="xs" color="danger" className="ms-1 py-0 px-1">
                    <i className="fa fa-info-circle" />
                  </Button>
                </div>

                <UncontrolledTooltip
                  target={overdueId}
                  placement="left"
                  container="body"
                  delay={{ show: 50, hide: 50 }}
                  autohide={false}
                  style={{ minWidth: '720px' }}
                >
                  <div className="text-start p-2">
                    <div className="fw-semibold mb-2">{t('leads.table.overdueTasks')}</div>
                    <div
                      className="table-responsive tooltip-table"
                      style={{ maxHeight: 320, overflowY: 'auto' }}
                    >
                      <table className="table table-sm table-striped mb-0 align-middle avg-tooltip">
                        <thead className="table-light">
                          <tr>
                            <th className="px-1" style={{ width: 20 }}>
                              #
                            </th>

                            <th className="px-1" style={{ minWidth: 180 }}>
                              {t('leads.table.action')}
                            </th>
                            {/* <th className="px-1">{t('leads.table.taskStage')}</th> */}

                            <th className="px-1">{t('leads.table.taskTypeTitle')}</th>

                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.deadline')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.doneAt')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.hoursLate')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadOverdues.map((d, idx) => (
                            <tr key={`${row.id}-ov-${idx}`}>
                              <td className="px-1">{idx + 1}</td>

                              <td className="px-1" style={{ maxWidth: 260 }} title={d.task}>
                                {d.task}
                              </td>
                              {/* <td className="px-1" style={{ maxWidth: 260 }} title={d.stageStatus}>
                                {t(`leads.table.${d.stageStatus}`)}
                              </td> */}
                              <td className="px-1" style={{ maxWidth: 260 }} title={d.taskType}>
                                {t(`leads.table.taskType.${d.taskType}`)}
                              </td>
                              <td className="px-1">
                                {formatWithOffset(d.deadline, tz, undefined)}
                              </td>
                              <td className="px-1">
                                {d.whenDone ? (
                                  formatWithOffset(d.whenDone, tz, undefined)
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="px-1 text-danger">{d.hoursLate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];
};
export const getEngagedTableColumns2 = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<EngagementsTableData>[] => {
  const hasComment = (c: unknown): boolean => typeof c === 'string' && c.trim().length > 0;

  const isWaiting = (d: EngagementsTableData['flowData'][number]) => !hasComment(d.comment);

  const nearestTs = (row: EngagementsTableData) => {
    const waiting = (row.flowData ?? []).filter(isWaiting);
    if (!waiting.length) return Number.MAX_SAFE_INTEGER; // в кінець таблиці
    return Math.min(
      ...waiting
        .map((d) => Date.parse(d.deadline as unknown as string))
        .filter((ts) => Number.isFinite(ts)),
    );
  };

  return [
    {
      name: t('leads.table.name'),
      selector: (row) => row.fullName,
      cell: (row) => {
        const infoId = `engagenemts-info-${row.id}`;
        return (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <a href={`/leadslist/engagement/${row.id}`}>{row.fullName}</a>
            <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
              <i className="fa fa-info-circle" />
            </Button>
            <UncontrolledTooltip
              target={infoId}
              placement="left"
              container="body"
              delay={{ show: 50, hide: 50 }}
              style={{
                minWidth: '600px',
              }}
            >
              <div className="text-start d-flex flex-column">
                <span>Email: {row.email}</span>
                <span>Telegram: {row.telegram}</span>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
      sortable: true,
      sortField: 'fullname',
    },
    {
      name: t('engagements.table.watchDate'),
      selector: (row) => new Date(row.lastWatchedAt).getTime(),
      cell: (row) => formatWithOffset(row.lastWatchedAt, tz, undefined),
      sortable: true,
      sortField: 'watchDate',
    },
    {
      name: t('leads.table.nearestDeadline'),
      selector: nearestTs, // numeric timestamp for sorting
      sortField: 'nextDeadline',
      sortable: true,
      width: '360px',
      cell: (row) => {
        const tz = 'Europe/Kyiv'; // або ваша змінна
        const infoId = `deadlines-info-${row.id}`;
        const overdueId = `overdues-info-${row.id}`;

        const hasComment = (c: unknown) => typeof c === 'string' && c.trim().length > 0;
        const hasFiles = (f: unknown) => Array.isArray(f) && f.length > 0;

        // 1) Чекаючі (без коментаря)
        const waiting = (row.flowData ?? []).filter((d) => !hasComment(d.comment));

        // Найближча серед “чекаючих”
        const n =
          waiting.length > 0
            ? [...waiting].sort(
                (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
              )[0]
            : null;

        // 2) Просрочки: без комента й дедлайн у минулому АБО комент після дедлайну
        const nowMs = Date.now();
        const toMs = (v: string | Date | null | undefined) =>
          v ? new Date(v as any).getTime() : NaN;
        const round2 = (x: number) => Math.round(x * 100) / 100;
        const leadOverdues = (row.flowData ?? [])
          .filter((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const deadlinePassed = Number.isFinite(dlMs) && nowMs > dlMs;
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            return (deadlinePassed && !_hasComment) || overdueComment;
          })
          .map((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            const hoursLate = overdueComment
              ? round2((updMs - dlMs) / 3_600_000)
              : round2((nowMs - dlMs) / 3_600_000);
            return { ...d, whenDone: _hasComment ? d.updatedAt : null, hoursLate };
          })
          .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
        return (
          <div className="w-100">
            <div className="w-100 d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span>
                  {waiting.length === 0
                    ? '—'
                    : waiting.length > 1
                      ? `${waiting.length} шт.`
                      : `${n!.remaining} ${t('global.h')} ${formatWithOffset(n!.deadline, tz, undefined)}`}
                </span>
              </div>

              {/* info button with a tooltip listing upcoming deadlines */}
              <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={infoId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '600px',
                }}
              >
                <div className="text-start">
                  <div className="fw-semibold mb-1">{t('leads.table.upcomingTasks', 'Задачі')}</div>
                  {waiting?.length ? (
                    <ul className="mb-0 ps-3">
                      {waiting
                        .slice() // don’t mutate
                        .sort(
                          (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
                        )
                        .map((d, i) => (
                          <li key={`${row.id}-${i}`} className="d-block p-0">
                            {i + 1}
                            {')'} {d.task}: {d.remaining} {t('global.h')} {t('global.untill')}{' '}
                            {formatWithOffset(d.deadline, tz, undefined)}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <span>{t('leads.noDeadlines', 'No pending tasks')}</span>
                  )}
                </div>
              </UncontrolledTooltip>
            </div>
            {/* Line 2: Просрочки — X шт. (i) (ONLY if there are overdues) */}
            {leadOverdues.length > 0 && (
              <div className="w-100 d-flex justify-content-between align-items-center">
                <div className="small text-danger">
                  {t('leads.table.overduesLabel')} — {leadOverdues.length}{' '}
                  {t('global.qtyShort', 'шт.')}{' '}
                  <Button id={overdueId} size="xs" color="danger" className="ms-1 py-0 px-1">
                    <i className="fa fa-info-circle" />
                  </Button>
                </div>

                <UncontrolledTooltip
                  target={overdueId}
                  placement="left"
                  container="body"
                  delay={{ show: 50, hide: 50 }}
                  autohide={false}
                  style={{ minWidth: '720px' }}
                >
                  <div className="text-start p-2">
                    <div className="fw-semibold mb-2">{t('leads.table.overdueTasks')}</div>
                    <div
                      className="table-responsive tooltip-table"
                      style={{ maxHeight: 320, overflowY: 'auto' }}
                    >
                      <table className="table table-sm table-striped mb-0 align-middle avg-tooltip">
                        <thead className="table-light">
                          <tr>
                            <th className="px-1" style={{ width: 20 }}>
                              #
                            </th>

                            <th className="px-1" style={{ minWidth: 180 }}>
                              {t('leads.table.action')}
                            </th>
                            {/* <th className="px-1">{t('leads.table.taskStage')}</th> */}

                            <th className="px-1">{t('leads.table.taskTypeTitle')}</th>

                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.deadline')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.doneAt')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.hoursLate')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadOverdues.map((d, idx) => (
                            <tr key={`${row.id}-ov-${idx}`}>
                              <td className="px-1">{idx + 1}</td>

                              <td className="px-1" style={{ maxWidth: 260 }} title={d.task}>
                                {d.task}
                              </td>
                              {/* <td className="px-1" style={{ maxWidth: 260 }} title={d.stageStatus}>
                                {t(`leads.table.${d.stageStatus}`)}
                              </td> */}
                              <td className="px-1" style={{ maxWidth: 260 }} title={d.taskType}>
                                {t(`leads.table.taskType.${d.taskType}`)}
                              </td>
                              <td className="px-1">
                                {formatWithOffset(d.deadline, tz, undefined)}
                              </td>
                              <td className="px-1">
                                {d.whenDone ? (
                                  formatWithOffset(d.whenDone, tz, undefined)
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="px-1 text-danger">{d.hoursLate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];
};

export const getLeadsEngagementsOverdueTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<OverdueItem>[] => {
  return [
    {
      name: t('control.overduesTable.userName'),
      selector: (row) => row.username ?? 'No name',
      cell: (row) => {
        const infoId = `engagenemts-info-${row.id}`;
        const url =
          row.source === 'lead' ? `leadslist/details/${row.id}` : `/leadslist/engagement/${row.id}`;
        return (
          <div className="d-flex justify-content-between w-100 align-items-center">
            <a href={url}>{row.username}</a>
            <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
              <i className="fa fa-info-circle" />
            </Button>
            <UncontrolledTooltip
              target={infoId}
              placement="left"
              container="body"
              delay={{ show: 50, hide: 50 }}
              style={{
                minWidth: '600px',
              }}
            >
              <div className="text-start d-flex flex-column">
                <span>Email: {row.email}</span>
                <span>Telegram: {row.telegram}</span>
              </div>
            </UncontrolledTooltip>
          </div>
        );
      },
    },
    {
      name: t('control.overduesTable.source'),
      selector: (row) => row.source,
      cell: (row) => <span>{t(`control.overduesTable.${row.source}`)}</span>,
    },
    {
      name: t('control.overduesTable.deadline'),
      selector: (row) => {
        const hasComment = (c: unknown): boolean => typeof c === 'string' && c.trim().length > 0;
        const isWaiting = (d: OverdueItem['deadlines'][number]) => !hasComment(d.comment);
        const waiting = (row.deadlines ?? []).filter(isWaiting);
        if (!waiting.length) return Number.MAX_SAFE_INTEGER;
        return Math.min(
          ...waiting
            .map((d) => Date.parse(d.deadline as unknown as string))
            .filter((ts) => Number.isFinite(ts)),
        );
      },
      sortField: 'nextDeadline',
      sortable: true,
      width: '360px',
      cell: (row) => {
        const infoId = `deadlines-info-${row.id}`;
        const overdueId = `overdues-info-${row.id}`;
        const { source } = row;

        const hasComment = (c: unknown) => typeof c === 'string' && c.trim().length > 0;

        // 1) Чекаючі (без коментаря)
        const waiting = (row.deadlines ?? []).filter((d) => !hasComment(d.comment));

        // Найближча серед "чекаючих"
        const n =
          waiting.length > 0
            ? [...waiting].sort(
                (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
              )[0]
            : null;

        // 2) Просрочки: без комента й дедлайн у минулому АБО комент після дедлайну
        const nowMs = Date.now();
        const toMs = (v: string | Date | null | undefined) =>
          v ? new Date(v as any).getTime() : NaN;
        const round2 = (x: number) => Math.round(x * 100) / 100;

        const leadOverdues = (row.deadlines ?? [])
          .filter((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const deadlinePassed = Number.isFinite(dlMs) && nowMs > dlMs;
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            return (deadlinePassed && !_hasComment) || overdueComment;
          })
          .map((d) => {
            const dlMs = toMs(d.deadline);
            const updMs = toMs(d.updatedAt);
            const _hasComment = hasComment(d.comment);
            const overdueComment = _hasComment && Number.isFinite(updMs) && updMs > dlMs;
            const hoursLate = overdueComment
              ? round2((updMs - dlMs) / 3_600_000)
              : round2((nowMs - dlMs) / 3_600_000);
            return { ...d, whenDone: _hasComment ? d.updatedAt : null, hoursLate };
          })
          .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
        console.log('name', row.username, 'overdues', leadOverdues.length);
        return (
          <div className="w-100">
            <div className="w-100 d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                <span>
                  {waiting.length === 0
                    ? '—'
                    : waiting.length > 1
                      ? `${waiting.length} шт.`
                      : `${n!.remaining} ${t('global.h')} ${formatWithOffset(n!.deadline, tz, undefined)}`}
                </span>
              </div>

              {/* info button with a tooltip listing upcoming deadlines */}
              <Button id={infoId} size="xs" color="primary" className="ms-auto me-0">
                <i className="fa fa-info-circle" />
              </Button>
              <UncontrolledTooltip
                target={infoId}
                placement="left"
                container="body"
                delay={{ show: 50, hide: 50 }}
                style={{
                  minWidth: '600px',
                }}
              >
                <div className="text-start">
                  <div className="fw-semibold mb-1">{t('leads.table.upcomingTasks', 'Задачі')}</div>
                  {waiting?.length ? (
                    <ul className="mb-0 ps-3">
                      {waiting
                        .slice()
                        .sort(
                          (a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime(),
                        )
                        .map((d, i) => (
                          <li key={`${row.id}-${i}`} className="d-block p-0">
                            {i + 1}
                            {')'} {d.task}: {d.remaining} {t('global.h')} {t('global.untill')}{' '}
                            {formatWithOffset(d.deadline, tz, undefined)}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <span>{t('leads.noDeadlines', 'No pending tasks')}</span>
                  )}
                </div>
              </UncontrolledTooltip>
            </div>
            {/* Line 2: Просрочки — X шт. (i) (ONLY if there are overdues) */}
            {leadOverdues.length > 0 && (
              <div className="w-100 d-flex justify-content-between align-items-center">
                <div className="small text-danger">
                  {t('leads.table.overduesLabel')} — {leadOverdues.length}{' '}
                  {t('global.qtyShort', 'шт.')}{' '}
                  <Button id={overdueId} size="xs" color="danger" className="ms-1 py-0 px-1">
                    <i className="fa fa-info-circle" />
                  </Button>
                </div>

                <UncontrolledTooltip
                  target={overdueId}
                  placement="left"
                  container="body"
                  delay={{ show: 50, hide: 50 }}
                  autohide={false}
                  style={{ minWidth: '720px' }}
                >
                  <div className="text-start p-2">
                    <div className="fw-semibold mb-2">{t('leads.table.overdueTasks')}</div>
                    <div
                      className="table-responsive tooltip-table"
                      style={{ maxHeight: 320, overflowY: 'auto' }}
                    >
                      <table className="table table-sm table-striped mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="px-1" style={{ width: 20 }}>
                              #
                            </th>

                            <th className="px-1" style={{ minWidth: 180 }}>
                              {t('leads.table.action')}
                            </th>
                            {source === 'lead' && (
                              <th className="px-1">{t('leads.table.taskStage')}</th>
                            )}

                            <th className="px-1">{t('leads.table.taskTypeTitle')}</th>

                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.deadline')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.doneAt')}
                            </th>
                            <th className="px-1" style={{ width: 160 }}>
                              {t('leads.table.hoursLate')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadOverdues.map((d, idx) => (
                            <tr key={`${row.id}-ov-${idx}`}>
                              <td className="px-1">{idx + 1}</td>

                              <td className="px-1" style={{ maxWidth: 260 }} title={d.task}>
                                {d.task}
                              </td>
                              {source === 'lead' && (
                                <td className="px-1" style={{ maxWidth: 260 }} title={d.stage}>
                                  {t(`leads.table.${d.stage}`)}
                                </td>
                              )}
                              <td className="px-1" style={{ maxWidth: 260 }} title={d.taskType}>
                                {t(`leads.table.taskType.${d.taskType}`)}
                              </td>
                              <td className="px-1">
                                {formatWithOffset(d.deadline, tz, undefined)}
                              </td>
                              <td className="px-1">
                                {d.whenDone ? (
                                  formatWithOffset(d.whenDone, tz, undefined)
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="px-1 text-danger">{d.hoursLate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </UncontrolledTooltip>
              </div>
            )}
          </div>
        );
      },
    },
  ];
};

export const getCrmControlTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
  handleOpenSelectModal?: (controlId: number, source: string) => void,
  handleHistoryOpen?: (controlId: number) => void,
): TableColumn<CrmControlItem>[] => {
  return [
    {
      name: t('control.crmTable.userName'),
      selector: (row) => row.fullName ?? 'No name',
      cell: (row) => {
        if (row.entityId === null) {
          return <span>{row.fullName}</span>;
        }
        let url = '#';

        if (row.source === 'lead') {
          url = `/leadslist/details/${row.entityId}`;
        } else if (row.source === 'engagement') {
          url = `/leadslist/engagement/${row.entityId}`;
        } else if (row.source === 'exam') {
          url = `/exams/details/${row.entityId}`;
        }
        // const url = `/crm/details/${row.controlId}`;
        return <a href={url}>{row.fullName}</a>;
      },
      sortField: 'fullName',
      sortable: true,
    },
    {
      name: t('control.crmTable.source.title'),
      selector: (row) => row.source,
      cell: (row) => <span>{t(`control.crmTable.source.${row.source}`)}</span>,
      sortField: 'source',
    },
    {
      name: t('control.crmTable.reason.title'),
      selector: (row) => row.reason,
      cell: (row) => {
        const overdueId = `reason-info-${row.controlId}`;
        return (
          <div>
            <span id={overdueId}>{t(`control.crmTable.reason.${row.reason}`)}</span>
            <UncontrolledTooltip
              target={overdueId}
              placement="top"
              container="body"
              delay={{ show: 50, hide: 50 }}
              autohide={false}
              style={{ minWidth: '250px', textAlign: 'left' }}
            >
              {t(`control.crmTable.headingTooltip.violationDescriptionDetails.${row.reason}`)}
            </UncontrolledTooltip>
          </div>
        );
      },
      sortField: 'reason',
    },
    {
      name: t('control.crmTable.violationDate'),
      selector: (row) => new Date(row.createdAt).getTime(),
      cell: (row) => formatWithOffset(row.createdAt, tz, undefined),
      sortField: 'violationDate',
      sortable: true,
    },
    {
      name: t('control.crmTable.action.title'),
      selector: (row) => row.statusName,
      cell: (row) => {
        const bgCode = row.colorCode;
        return (
          <div className="d-flex justify-content-between align-items-center w-100">
            <Chip
              bgColor={bgCode ? `${bgCode}80` : undefined}
              textColor={'inherit'}
              label={t(`global.selectorStatus.${row.statusName}`, row.statusName)}
              borderRadius="2px"
            />

            <div className="d-flex align-items-center">
              <Button
                color="primary"
                outline
                size="xs"
                onClick={(e) => {
                  handleOpenSelectModal && handleOpenSelectModal(row.controlId, row.source);
                }}
                className="me-2"
              >
                <i className="fa fa-comment" />
              </Button>
              <Button
                color="primary"
                // className='d-flex justify-content-center align-items-center h-auto'
                outline
                size="xs"
                onClick={(e) => {
                  handleHistoryOpen && handleHistoryOpen(row.controlId);
                }}
              >
                <i className="fa fa-history"></i>
              </Button>
            </div>
          </div>
        );
      },
      sortField: 'statusName',
      sortable: true,
      width: '250px',
    },
  ];
};

export const getCrmControlHistoryTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<CrmControlItemActionHistoryItem>[] => {
  return [
    {
      name: t('control.historyTable.actionNamePrev'),
      selector: (row) => row.action,
      cell: (row, i) => {
        const bgColor = row.prevColorCode || '#e0e0e0';
        return (
          <Chip
            bgColor={bgColor ? `${bgColor}80` : undefined}
            textColor={'inherit'}
            label={
              row.actionNamePrev
                ? t(`global.selectorStatus.${row.actionNamePrev}`, {
                    defaultValue: row.actionNamePrev,
                  })
                : '-'
            }
            borderRadius="2px"
          />
        );
      },
      sortable: false,
    },
    {
      name: t('control.historyTable.actionNameNew'),
      selector: (row) => row.action,
      cell: (row) => {
        const bgColor = row.colorCode || '#e0e0e0';
        return (
          <Chip
            bgColor={bgColor ? `${bgColor}80` : undefined}
            textColor={'inherit'}
            label={
              row.actionName
                ? t(`global.selectorStatus.${row.actionName}`, { defaultValue: row.actionName })
                : '-'
            }
            borderRadius="2px"
          />
        );
      },
      sortField: 'action',
      sortable: true,
    },

    {
      name: t('control.historyTable.oldComment'),
      selector: (row) => row.oldComment || '—',
      cell: (row) => <span>{row.oldComment || '—'}</span>,

      sortable: false,
    },
    {
      name: t('control.historyTable.screenshots'),
      selector: (row) => row.files?.length || 0,
      cell: (row) => (
        <div className="d-flex flex-column">
          {!row.files || row.files.length === 0 ? <span className="text-muted">—</span> : null}
          {row.files && row.files?.length > 0 && (
            <Gallery withCaption>
              <div className="d-flex flex-wrap gap-2 mt-2 mb-2">
                {row.files.map((p, idx) => {
                  const img = new window.Image();
                  const formatted = {
                    ...p,
                    w: img.naturalWidth || 1600,
                    h: img.naturalHeight || 1200,
                  };
                  return (
                    <figure key={`${p.url}-${idx}`} className="m-0 position-relative">
                      <Item
                        original={`${process.env.REACT_APP_BACKEND_STATIC}/${p.url}`}
                        width={formatted.w}
                        height={formatted.h}
                        caption={p.name}
                      >
                        {({ ref, open }: any) => (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              open();
                            }}
                          >
                            <img
                              ref={ref as React.Ref<HTMLImageElement>}
                              src={`${process.env.REACT_APP_BACKEND_STATIC}/${p.url}`}
                              alt={p.name ?? 'preview'}
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 6,
                                display: 'block',
                              }}
                            />
                          </a>
                        )}
                      </Item>
                    </figure>
                  );
                })}
              </div>
            </Gallery>
          )}
        </div>
      ),
      sortable: false,
    },
    {
      name: t('control.historyTable.date'),
      selector: (row) => new Date(row.timestamp).getTime(),
      cell: (row) => formatWithOffset(row.timestamp, tz, undefined),
      sortField: 'timestamp',
      sortable: true,
    },
  ];
};
export const getUnprocessedTransactionsTableColumns = (
  t: TFunction,
  tz: Intl.DateTimeFormatOptions['timeZone'] = 'Europe/Kyiv',
): TableColumn<UnprocessedTranscriptionItem>[] => {
  return [
    {
      name: t('control.unprocessed.table.userName'),
      selector: (row) => row.assistantExam?.user?.lastName ?? 'No name',
      cell: (row) => {
        const url = `/exams/details/${row.assistantExam?.id}`;
        return (
          <a href={url}>
            {row.assistantExam?.user?.firstName} {row.assistantExam?.user?.lastName}
          </a>
        );
      },
      sortField: 'lastName',
      sortable: true,
    },
    {
      name: t('assistantExam.table.examDate'),
      selector: (row) => new Date(row.assistantExam?.examDate).getTime(),
      cell: (row) => {
      
        if (!row.assistantExam?.examDate) {
          return <span className="text-muted">—</span>;
        }
        return formatWithOffset(row.assistantExam?.examDate, tz, undefined);
      },
      sortField: 'examDate',
      sortable: true,
    },
    {
      name: t('control.unprocessed.table.status'),
      selector: (row) => row.transcriptionProcessStatus,
      cell: (row) => {
        return (
          <span>
            {t(`control.unprocessed.transcriptionProcessStatus.${row.transcriptionProcessStatus}`)}
          </span>
        );
      },
      sortField: 'transcriptionProcessStatus',
      sortable: true,
    },
    {
      name: t('control.unprocessed.table.createdAt'),
      selector: (row) => new Date(row?.createdAt).getTime(),
      cell: (row) => {
        if (!row?.createdAt) {
          return <span className="text-muted">—</span>;
        }
        return formatWithOffset(row?.createdAt, tz, undefined);
      },
      sortField: 'createdAt',
      sortable: true,
    },
    {
      name: t('control.unprocessed.table.updatedAt'),
      selector: (row) => new Date(row?.updatedAt).getTime(),
      cell: (row) => {
        if (!row?.updatedAt) {
          return <span className="text-muted">—</span>;
        }
        return formatWithOffset(row.updatedAt, tz, undefined);
      },
      sortField: 'updatedAt',
      sortable: true,
    },
    // {
    //   name: 'Bot id',
    //   selector: (row) => row.botId,
    //   cell: (row) => {
    //     return <span>{row.botId}</span>;
    //   },
    // },
  ];
};

export const AjaxSourcedColumns: TableColumn<AjaxSourced>[] = [
  {
    name: 'Name',
    selector: (row) => row.name,
    sortable: true,
  },
  {
    name: 'Position',
    selector: (row) => row.position,
    sortable: true,
  },
  {
    name: 'Office',
    selector: (row) => row.office,
    sortable: true,
  },
  {
    name: 'Extn.',
    selector: (row) => row.extends,
    sortable: true,
  },
  {
    name: 'Start Date',
    selector: (row) => row.date,
    sortable: true,
  },
  {
    name: 'Salary',
    selector: (row) => row.salary,
    sortable: true,
  },
];

export const AjaxSourcedTableData: AjaxSourced[] = [
  {
    id: 1,
    name: 'Angelica Ramos',
    position: 'Chief Executive Officer (CEO)',
    office: 'London',
    extends: '5797',
    date: '2009/10/09',
    salary: '$1,200,000',
  },
  {
    id: 2,
    name: 'Archana Desai',
    position: 'Accountant',
    office: 'Tokyo',
    extends: '5407',
    date: '2008/11/28',
    salary: '$162,700',
  },
  {
    id: 3,
    name: 'Ashton Cox',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    extends: '1562',
    date: '2009/01/12',
    salary: '$86,000',
  },
  {
    id: 4,
    name: 'Bradley Greer',
    position: 'Software Engineer',
    office: 'London',
    extends: '2558',
    date: '2012/10/13',
    salary: '$132,000',
  },
  {
    id: 5,
    name: 'Brenden Wagner',
    position: 'Software Engineer',
    office: 'San Francisco',
    extends: '1314',
    date: '2011/06/07',
    salary: '$206,850',
  },
  {
    id: 6,
    name: 'Bruno Nash',
    position: 'Software Engineer',
    office: 'London',
    extends: '6222',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 7,
    name: 'Caesar Vance',
    position: 'Pre-Sales Support',
    office: 'New York',
    extends: '8330',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 8,
    name: 'Cara Stevens',
    position: 'Sales Assistant',
    office: 'New York',
    extends: '3990',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 9,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    extends: '6224',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 10,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    extends: '6741',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 11,
    name: 'Dai Rios',
    position: 'Personnel Lead',
    office: 'Edinburgh',
    extends: '2290',
    date: '2012/09/26',
    salary: '$217,500',
  },
  {
    id: 12,
    name: 'Doris Wilder',
    position: 'Sales Assistant',
    office: 'Sidney',
    extends: '3023',
    date: '2010/09/20',
    salary: '$85,600',
  },
  {
    id: 13,
    name: 'Fiona Green',
    position: 'Chief Executive Officer (CEO)',
    office: 'San Francisco',
    extends: '2947',
    date: '2010/03/11',
    salary: '$850,000',
  },
  {
    id: 14,
    name: 'Garrett Winters',
    position: 'Accountant',
    office: 'Tokyo',
    extends: '8422',
    date: '2011/07/25',
    salary: '$170,750',
  },
  {
    id: 15,
    name: 'Gavin Cortez',
    position: 'Team Leader',
    office: 'San Francisco',
    extends: '2860',
    date: '2008/10/26',
    salary: '$235,500',
  },
  {
    id: 16,
    name: 'Gavin Joyce',
    position: 'Developer',
    office: 'Edinburgh',
    extends: '8822',
    date: '2010/12/22',
    salary: '$92,575',
  },
  {
    id: 17,
    name: 'Gloria Little',
    position: 'Systems Administrator',
    office: 'New York',
    extends: '1721',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 18,
    name: 'Haley Kennedy',
    position: 'Senior Marketing Designer',
    office: 'London',
    extends: '3597',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 19,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    extends: '9608',
    date: '2012/08/06',
    salary: '$137,500',
  },
  {
    id: 20,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    extends: '3814',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 21,
    name: 'Gloria Little',
    position: 'Systems Administrator',
    office: 'New York',
    extends: '1721',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 22,
    name: 'Haley Kennedy',
    position: 'Senior Marketing Designer',
    office: 'London',
    extends: '3597',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 23,
    name: 'Hermione Butler',
    position: 'Regional Director',
    office: 'London',
    extends: '1016',
    date: '2011/03/21',
    salary: '$356,250',
  },
  {
    id: 24,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    extends: '9608',
    date: '2012/08/06',
    salary: '$137,500',
  },

  {
    id: 25,
    name: 'Hope Fuentes',
    position: 'Secretary',
    office: 'San Francisco',
    extends: '6318',
    date: '2010/02/12',
    salary: '$109,850',
  },
  {
    id: 26,
    name: 'Howard Hatfield',
    position: 'Office Manager',
    office: 'San Francisco',
    extends: '7031',
    date: '2008/12/16',
    salary: '$164,500',
  },
  {
    id: 27,
    name: 'Jackson Bradshaw',
    position: 'Director',
    office: 'New York',
    extends: '1042',
    date: '2008/09/26',
    salary: '$645,750',
  },
  {
    id: 28,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    extends: '3814',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 29,
    name: 'Jenette Caldwell',
    position: 'Development Lead',
    office: 'New York',
    extends: '1937',
    date: '2011/09/03',
    salary: '$345,000',
  },
  {
    id: 30,
    name: 'Jennifer Acosta',
    position: 'Junior Javascript Developer',
    office: 'Edinburgh',
    extends: '3431',
    date: '2013/02/01',
    salary: '$75,650',
  },
  {
    id: 31,
    name: 'Bruno Nash',
    position: 'Software Engineer',
    office: 'London',
    extends: '6222',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 32,
    name: 'Caesar Vance',
    position: 'Pre-Sales Support',
    office: 'New York',
    extends: '8330',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 33,
    name: 'Cara Stevens',
    position: 'Sales Assistant',
    office: 'New York',
    extends: '3990',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 34,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    extends: '6224',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 35,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    extends: '6741',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 36,
    name: 'Archana Desai',
    position: 'Accountant',
    office: 'Tokyo',
    extends: '5407',
    date: '2008/11/28',
    salary: '$162,700',
  },
  {
    id: 37,
    name: 'Ashton Cox',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    extends: '1562',
    date: '2009/01/12',
    salary: '$86,000',
  },
  {
    id: 38,
    name: 'Bradley Greer',
    position: 'Software Engineer',
    office: 'London',
    extends: '2558',
    date: '2012/10/13',
    salary: '$132,000',
  },
  {
    id: 39,
    name: 'Brenden Wagner',
    position: 'Software Engineer',
    office: 'San Francisco',
    extends: '1314',
    date: '2011/06/07',
    salary: '$206,850',
  },
  {
    id: 40,
    name: 'Bruno Nash',
    position: 'Software Engineer',
    office: 'London',
    extends: '6222',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 41,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    extends: '9608',
    date: '2012/08/06',
    salary: '$137,500',
  },
  {
    id: 42,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    extends: '3814',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 43,
    name: 'Gloria Little',
    position: 'Systems Administrator',
    office: 'New York',
    extends: '1721',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 44,
    name: 'Haley Kennedy',
    position: 'Senior Marketing Designer',
    office: 'London',
    extends: '3597',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 45,
    name: 'Hermione Butler',
    position: 'Regional Director',
    office: 'London',
    extends: '1016',
    date: '2011/03/21',
    salary: '$356,250',
  },
  {
    id: 46,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    extends: '6224',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 47,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    extends: '6741',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 48,
    name: 'Dai Rios',
    position: 'Personnel Lead',
    office: 'Edinburgh',
    extends: '2290',
    date: '2012/09/26',
    salary: '$217,500',
  },
  {
    id: 49,
    name: 'Doris Wilder',
    position: 'Sales Assistant',
    office: 'Sidney',
    extends: '3023',
    date: '2010/09/20',
    salary: '$85,600',
  },
  {
    id: 50,
    name: 'Fiona Green',
    position: 'Chief Executive Officer (CEO)',
    office: 'San Francisco',
    extends: '2947',
    date: '2010/03/11',
    salary: '$850,000',
  },
  {
    id: 51,
    name: 'Jennifer Acosta',
    position: 'Junior Javascript Developer',
    office: 'Edinburgh',
    extends: '3431',
    date: '2013/02/01',
    salary: '$75,650',
  },
  {
    id: 52,
    name: 'Bruno Nash',
    position: 'Software Engineer',
    office: 'London',
    extends: '6222',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 53,
    name: 'Caesar Vance',
    position: 'Pre-Sales Support',
    office: 'New York',
    extends: '8330',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 54,
    name: 'Cara Stevens',
    position: 'Sales Assistant',
    office: 'New York',
    extends: '3990',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 55,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    extends: '6224',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 56,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    extends: '6741',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 57,
    name: 'Archana Desai',
    position: 'Accountant',
    office: 'Tokyo',
    extends: '5407',
    date: '2008/11/28',
    salary: '$162,700',
  },
];

const handlePDFClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
  const pdfUrl = `${process.env.PUBLIC_URL}/assets/pdf/sample.pdf`;
  window.open(pdfUrl, '_blank');
};

export const HtmlColumnsTableData: HtmlSourced[] = [
  {
    id: 1,
    name: 'Airi Satou',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'hired',
    email: 't.nixon@datatables.net',
    color: 'success',
    salary: '$162,700',
  },
  {
    id: 2,
    name: 'Ashton Cox',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    badge: 'hired',
    color: 'warning',
    email: 'a.cox@datatables.net',
    salary: '$86,000',
  },
  {
    id: 3,
    name: 'Brielle Williamson',
    position: 'Integration Specialist',
    office: 'New York',
    badge: 'Pending',
    color: 'danger',
    email: 'b.williamson@datatables.net',
    salary: '$372,000',
  },
  {
    id: 4,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'c.kelly@datatables.net',
    salary: '$433,060',
  },
  {
    id: 5,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    badge: 'in process',
    color: 'warning',
    email: 'c.marshall@datatables.net',
    salary: '$470,600',
  },
  {
    id: 6,
    name: 'Colleen Hurst',
    position: 'Javascript Developer',
    office: 'San Francisco',
    badge: 'Pending',
    color: 'danger',
    email: 'c.hurst@datatables.net',
    salary: '$205,500',
  },
  {
    id: 7,
    name: 'Donna Snider',
    position: 'Customer Support',
    office: 'New York',
    badge: 'hired',
    color: 'success',
    email: 'd.snider@datatables.net',
    salary: '$112,000',
  },
  {
    id: 8,
    name: 'Garrett Winters',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'Pending',
    color: 'danger',
    email: 'g.winters@datatables.net',
    salary: '$170,750',
  },
  {
    id: 9,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    badge: 'in process',
    color: 'warning',
    email: 'h.chandler@datatables.net',
    salary: '$137,500',
  },
  {
    id: 10,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$90,560',
  },
  {
    id: 11,
    name: 'Quinn Flynn',
    position: 'Support Lead',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'q.flynn@datatables.net',
    salary: '$342,000',
  },
  {
    id: 12,
    name: 'Rhona Davidson',
    position: 'Integration Specialist',
    office: 'Tokyo',
    badge: 'hired',
    color: 'success',
    email: 'r.davidson@datatables.net',
    salary: '$327,900',
  },
  {
    id: 13,
    name: 'Sonya Frost',
    position: 'Software Engineer',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 's.frost@datatables.net',
    salary: '$103,600',
  },
  {
    id: 14,
    name: 'Tiger Nixon',
    position: 'System Architect',
    office: 'Edinburgh',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$320,800',
  },
  {
    id: 15,
    name: 'Airi Satou',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'hired',
    email: 't.nixon@datatables.net',
    color: 'success',
    salary: '$162,700',
  },
  {
    id: 16,
    name: 'Ashton Cox',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    badge: 'hired',
    color: 'warning',
    email: 'a.cox@datatables.net',
    salary: '$86,000',
  },
  {
    id: 17,
    name: 'Brielle Williamson',
    position: 'Integration Specialist',
    office: 'New York',
    badge: 'Pending',
    color: 'danger',
    email: 'b.williamson@datatables.net',
    salary: '$372,000',
  },
  {
    id: 18,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'c.kelly@datatables.net',
    salary: '$433,060',
  },
  {
    id: 19,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    badge: 'in process',
    color: 'warning',
    email: 'c.marshall@datatables.net',
    salary: '$470,600',
  },
  {
    id: 20,
    name: 'Colleen Hurst',
    position: 'Javascript Developer',
    office: 'San Francisco',
    badge: 'Pending',
    color: 'danger',
    email: 'c.hurst@datatables.net',
    salary: '$205,500',
  },
  {
    id: 21,
    name: 'Donna Snider',
    position: 'Customer Support',
    office: 'New York',
    badge: 'hired',
    color: 'success',
    email: 'd.snider@datatables.net',
    salary: '$112,000',
  },
  {
    id: 22,
    name: 'Garrett Winters',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'Pending',
    color: 'danger',
    email: 'g.winters@datatables.net',
    salary: '$170,750',
  },
  {
    id: 23,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    badge: 'in process',
    color: 'warning',
    email: 'h.chandler@datatables.net',
    salary: '$137,500',
  },
  {
    id: 24,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$90,560',
  },
  {
    id: 25,
    name: 'Quinn Flynn',
    position: 'Support Lead',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'q.flynn@datatables.net',
    salary: '$342,000',
  },
  {
    id: 26,
    name: 'Rhona Davidson',
    position: 'Integration Specialist',
    office: 'Tokyo',
    badge: 'hired',
    color: 'success',
    email: 'r.davidson@datatables.net',
    salary: '$327,900',
  },
  {
    id: 27,
    name: 'Sonya Frost',
    position: 'Software Engineer',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 's.frost@datatables.net',
    salary: '$103,600',
  },
  {
    id: 28,
    name: 'Tiger Nixon',
    position: 'System Architect',
    office: 'Edinburgh',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$320,800',
  },
  {
    id: 29,
    name: 'Donna Snider',
    position: 'Customer Support',
    office: 'New York',
    badge: 'hired',
    color: 'success',
    email: 'd.snider@datatables.net',
    salary: '$112,000',
  },
  {
    id: 30,
    name: 'Garrett Winters',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'Pending',
    color: 'danger',
    email: 'g.winters@datatables.net',
    salary: '$170,750',
  },
  {
    id: 31,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    badge: 'in process',
    color: 'warning',
    email: 'h.chandler@datatables.net',
    salary: '$137,500',
  },
  {
    id: 32,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$90,560',
  },
  {
    id: 33,
    name: 'Quinn Flynn',
    position: 'Support Lead',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'q.flynn@datatables.net',
    salary: '$342,000',
  },
  {
    id: 34,
    name: 'Rhona Davidson',
    position: 'Integration Specialist',
    office: 'Tokyo',
    badge: 'hired',
    color: 'success',
    email: 'r.davidson@datatables.net',
    salary: '$327,900',
  },
  {
    id: 35,
    name: 'Sonya Frost',
    position: 'Software Engineer',
    office: 'Edinburgh',
    badge: 'in processwarning',
    color: 'warning',
    email: 's.frost@datatables.net',
    salary: '$103,600',
  },
  {
    id: 36,
    name: 'Tiger Nixon',
    position: 'System Architect',
    office: 'Edinburgh',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$320,800',
  },
  {
    id: 37,
    name: 'Airi Satou',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'hired',
    email: 't.nixon@datatables.net',
    color: 'success',
    salary: '$162,700',
  },
  {
    id: 38,
    name: 'Ashton Cox',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    badge: 'hired',
    color: 'warning',
    email: 'a.cox@datatables.net',
    salary: '$86,000',
  },
  {
    id: 39,
    name: 'Brielle Williamson',
    position: 'Integration Specialist',
    office: 'New York',
    badge: 'Pending',
    color: 'danger',
    email: 'b.williamson@datatables.net',
    salary: '$372,000',
  },
  {
    id: 40,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'c.kelly@datatables.net',
    salary: '$433,060',
  },
  {
    id: 41,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$90,560',
  },
  {
    id: 42,
    name: 'Quinn Flynn',
    position: 'Support Lead',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'q.flynn@datatables.net',
    salary: '$342,000',
  },
  {
    id: 43,
    name: 'Rhona Davidson',
    position: 'Integration Specialist',
    office: 'Tokyo',
    badge: 'hired',
    color: 'success',
    email: 'r.davidson@datatables.net',
    salary: '$327,900',
  },
  {
    id: 44,
    name: 'Sonya Frost',
    position: 'Software Engineer',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 's.frost@datatables.net',
    salary: '$103,600',
  },
  {
    id: 45,
    name: 'Tiger Nixon',
    position: 'System Architect',
    office: 'Edinburgh',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$320,800',
  },
  {
    id: 46,
    name: 'Donna Snider',
    position: 'Customer Support',
    office: 'New York',
    badge: 'hired',
    color: 'success',
    email: 'd.snider@datatables.net',
    salary: '$112,000',
  },
  {
    id: 47,
    name: 'Garrett Winters',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'Pending',
    color: 'danger',
    email: 'g.winters@datatables.net',
    salary: '$170,750',
  },
  {
    id: 48,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    badge: 'in process',
    color: 'warning',
    email: 'h.chandler@datatables.net',
    salary: '$137,500',
  },
  {
    id: 49,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    badge: 'hired',
    color: 'success',
    email: 'j.gaines@datatables.net',
    salary: '$90,560',
  },
  {
    id: 50,
    name: 'Quinn Flynn',
    position: 'Support Lead',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'q.flynn@datatables.net',
    salary: '$342,000',
  },
  {
    id: 51,
    name: 'Airi Satou',
    position: 'Accountant',
    office: 'Tokyo',
    badge: 'hired',
    email: 't.nixon@datatables.net',
    color: 'success',
    salary: '$162,700',
  },
  {
    id: 52,
    name: 'Ashton Cox',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    badge: 'hired',
    color: 'warning',
    email: 'a.cox@datatables.net',
    salary: '$86,000',
  },
  {
    id: 53,
    name: 'Brielle Williamson',
    position: 'Integration Specialist',
    office: 'New York',
    badge: 'Pending',
    color: 'danger',
    email: 'b.williamson@datatables.net',
    salary: '$372,000',
  },
  {
    id: 54,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    badge: 'in process',
    color: 'warning',
    email: 'c.kelly@datatables.net',
    salary: '$433,060',
  },
  {
    id: 55,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    badge: 'in process',
    color: 'warning',
    email: 'c.marshall@datatables.net',
    salary: '$470,600',
  },
  {
    id: 56,
    name: 'Colleen Hurst',
    position: 'Javascript Developer',
    office: 'San Francisco',
    badge: 'Pending',
    color: 'danger',
    email: 'c.hurst@datatables.net',
    salary: '$205,500',
  },
  {
    id: 57,
    name: 'Donna Snider',
    position: 'Customer Support',
    office: 'New York',
    badge: 'hired',
    color: 'success',
    email: 'd.snider@datatables.net',
    salary: '$112,000',
  },
];

export const JavascriptSourcedTableData: AjaxSourced[] = [
  {
    id: 1,
    name: 'Angelica Ramos',
    position: 'Chief Executive Officer (CEO)',
    office: 'London',
    extends: '5797',
    date: '2009/10/09',
    salary: '$1,200,000',
  },
  {
    id: 2,
    name: 'Archana Desai',
    position: 'Accountant',
    office: 'Tokyo',
    extends: '5407',
    date: '2008/11/28',
    salary: '$162,700',
  },
  {
    id: 3,
    name: 'Ashton Cox',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    extends: '1562',
    date: '2009/01/12',
    salary: '$86,000',
  },
  {
    id: 4,
    name: 'Bradley Greer',
    position: 'Software Engineer',
    office: 'London',
    extends: '2558',
    date: '2012/10/13',
    salary: '$132,000',
  },
  {
    id: 5,
    name: 'Brenden Wagner',
    position: 'Software Engineer',
    office: 'San Francisco',
    extends: '1314',
    date: '2011/06/07',
    salary: '$206,850',
  },
  {
    id: 6,
    name: 'Bruno Nash',
    position: 'Software Engineer',
    office: 'London',
    extends: '6222',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 7,
    name: 'Caesar Vance',
    position: 'Pre-Sales Support',
    office: 'New York',
    extends: '8330',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 8,
    name: 'Cara Stevens',
    position: 'Sales Assistant',
    office: 'New York',
    extends: '3990',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 9,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    extends: '6224',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 10,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    extends: '6741',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 11,
    name: 'Dai Rios',
    position: 'Personnel Lead',
    office: 'Edinburgh',
    extends: '2290',
    date: '2012/09/26',
    salary: '$217,500',
  },
  {
    id: 12,
    name: 'Doris Wilder',
    position: 'Sales Assistant',
    office: 'Sidney',
    extends: '3023',
    date: '2010/09/20',
    salary: '$85,600',
  },
  {
    id: 13,
    name: 'Fiona Green',
    position: 'Chief Executive Officer (CEO)',
    office: 'San Francisco',
    extends: '2947',
    date: '2010/03/11',
    salary: '$850,000',
  },
  {
    id: 14,
    name: 'Garrett Winters',
    position: 'Accountant',
    office: 'Tokyo',
    extends: '8422',
    date: '2011/07/25',
    salary: '$170,750',
  },
  {
    id: 15,
    name: 'Gavin Cortez',
    position: 'Team Leader',
    office: 'San Francisco',
    extends: '2860',
    date: '2008/10/26',
    salary: '$235,500',
  },
  {
    id: 16,
    name: 'Gavin Joyce',
    position: 'Developer',
    office: 'Edinburgh',
    extends: '8822',
    date: '2010/12/22',
    salary: '$92,575',
  },
  {
    id: 17,
    name: 'Gloria Little',
    position: 'Systems Administrator',
    office: 'New York',
    extends: '1721',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 18,
    name: 'Haley Kennedy',
    position: 'Senior Marketing Designer',
    office: 'London',
    extends: '3597',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 19,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    extends: '9608',
    date: '2012/08/06',
    salary: '$137,500',
  },
  {
    id: 20,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    extends: '3814',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 21,
    name: 'Gloria Little',
    position: 'Systems Administrator',
    office: 'New York',
    extends: '1721',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 22,
    name: 'Haley Kennedy',
    position: 'Senior Marketing Designer',
    office: 'London',
    extends: '3597',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 23,
    name: 'Hermione Butler',
    position: 'Regional Director',
    office: 'London',
    extends: '1016',
    date: '2011/03/21',
    salary: '$356,250',
  },
  {
    id: 24,
    name: 'Herrod Chandler',
    position: 'Sales Assistant',
    office: 'San Francisco',
    extends: '9608',
    date: '2012/08/06',
    salary: '$137,500',
  },

  {
    id: 25,
    name: 'Hope Fuentes',
    position: 'Secretary',
    office: 'San Francisco',
    extends: '6318',
    date: '2010/02/12',
    salary: '$109,850',
  },
  {
    id: 26,
    name: 'Howard Hatfield',
    position: 'Office Manager',
    office: 'San Francisco',
    extends: '7031',
    date: '2008/12/16',
    salary: '$164,500',
  },
  {
    id: 27,
    name: 'Jackson Bradshaw',
    position: 'Director',
    office: 'New York',
    extends: '1042',
    date: '2008/09/26',
    salary: '$645,750',
  },
  {
    id: 28,
    name: 'Jena Gaines',
    position: 'Office Manager',
    office: 'London',
    extends: '3814',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 29,
    name: 'Jenette Caldwell',
    position: 'Development Lead',
    office: 'New York',
    extends: '1937',
    date: '2011/09/03',
    salary: '$345,000',
  },
  {
    id: 30,
    name: 'Jennifer Acosta',
    position: 'Junior Javascript Developer',
    office: 'Edinburgh',
    extends: '3431',
    date: '2013/02/01',
    salary: '$75,650',
  },
  {
    id: 31,
    name: 'Bruno Nash',
    position: 'Software Engineer',
    office: 'London',
    extends: '6222',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 32,
    name: 'Caesar Vance',
    position: 'Pre-Sales Support',
    office: 'New York',
    extends: '8330',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 33,
    name: 'Cara Stevens',
    position: 'Sales Assistant',
    office: 'New York',
    extends: '3990',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 34,
    name: 'Cedric Kelly',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    extends: '6224',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 35,
    name: 'Charde Marshall',
    position: 'Regional Director',
    office: 'San Francisco',
    extends: '6741',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 36,
    name: 'Archana Desai',
    position: 'Accountant',
    office: 'Tokyo',
    extends: '5407',
    date: '2008/11/28',
    salary: '$162,700',
  },
];

export const ServerSideProcessingColumns: TableColumn<ServerSideProcess>[] = [
  {
    name: 'First Name',
    selector: (row) => row.name,
    sortable: true,
  },
  {
    name: 'Last Name',
    selector: (row) => row.lastName,
    sortable: true,
  },
  {
    name: 'Position',
    selector: (row) => row.position,
    sortable: true,
  },
  {
    name: 'Office',
    selector: (row) => row.office,
    sortable: true,
  },
  {
    name: 'Start Date',
    selector: (row) => row.date,
    sortable: true,
  },
  {
    name: 'Salary',
    selector: (row) => row.salary,
    sortable: true,
  },
];

export const ServerSideData: ServerSideProcess[] = [
  {
    id: 1,
    name: 'Angelica',
    position: 'Chief Executive Officer (CEO)',
    office: 'London',
    lastName: 'Ramos',
    date: '2009/10/09',
    salary: '$1,200,000',
  },
  {
    id: 2,
    name: 'Archana',
    position: 'Accountant',
    office: 'Tokyo',
    lastName: 'Desai',
    date: '2008/11/28',
    salary: '$162,700',
  },
  {
    id: 3,
    name: 'Bradley',
    position: 'Software Engineer',
    office: 'London',
    lastName: 'Greer',
    date: '2012/10/13',
    salary: '$132,000',
  },
  {
    id: 4,
    name: 'Brenden',
    position: 'Software Engineer',
    office: 'San Francisco',
    lastName: 'Wagner',
    date: '2011/06/07',
    salary: '$206,850',
  },
  {
    id: 5,
    name: 'Bruno',
    position: 'Software Engineer',
    office: 'London',
    lastName: 'Nash',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 6,
    name: 'Caesar',
    position: 'Pre-Sales Support',
    office: 'New York',
    lastName: 'Vance',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 7,
    name: 'Cara',
    position: 'Sales Assistant',
    office: 'New York',
    lastName: 'Stevens',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 8,
    name: 'Cedric',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    lastName: 'Kelly',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 9,
    name: 'Charde',
    position: 'Regional Director',
    office: 'San Francisco',
    lastName: 'Marshall',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 10,
    name: 'Dai',
    position: 'Personnel Lead',
    office: 'Edinburgh',
    lastName: 'Rios',
    date: '2012/09/26',
    salary: '$217,500',
  },
  {
    id: 11,
    name: 'Doris',
    position: 'Sales Assistant',
    office: 'Sidney',
    lastName: 'Wilder',
    date: '2010/09/20',
    salary: '$85,600',
  },
  {
    id: 12,
    name: 'Fiona',
    position: 'Chief Executive Officer (CEO)',
    office: 'San Francisco',
    lastName: 'Green',
    date: '2010/03/11',
    salary: '$850,000',
  },
  {
    id: 13,
    name: 'Garrett',
    position: 'Accountant',
    office: 'Tokyo',
    lastName: 'Winters',
    date: '2011/07/25',
    salary: '$170,750',
  },
  {
    id: 14,
    name: 'Gavin',
    position: 'Team Leader',
    office: 'San Francisco',
    lastName: 'Cortez',
    date: '2008/10/26',
    salary: '$235,500',
  },
  {
    id: 15,
    name: 'Gavin',
    position: 'Developer',
    office: 'Edinburgh',
    lastName: 'Joyce',
    date: '2010/12/22',
    salary: '$92,575',
  },
  {
    id: 16,
    name: 'Gloria',
    position: 'Systems Administrator',
    office: 'New York',
    lastName: 'Little',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 17,
    name: 'Haley',
    position: 'Senior Marketing Designer',
    office: 'London',
    lastName: 'Kennedy',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 18,
    name: 'Herrod',
    position: 'Sales Assistant',
    office: 'San Francisco',
    lastName: 'Chandler',
    date: '2012/08/06',
    salary: '$137,500',
  },
  {
    id: 19,
    name: 'Jena',
    position: 'Office Manager',
    office: 'London',
    lastName: 'Gaines',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 20,
    name: 'Gloria',
    position: 'Systems Administrator',
    office: 'New York',
    lastName: 'Little',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 21,
    name: 'Haley',
    position: 'Senior Marketing Designer',
    office: 'London',
    lastName: 'Kennedy',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 22,
    name: 'Hermione',
    position: 'Regional Director',
    office: 'London',
    lastName: 'Butler',
    date: '2011/03/21',
    salary: '$356,250',
  },
  {
    id: 23,
    name: 'Herrod',
    position: 'Sales Assistant',
    office: 'San Francisco',
    lastName: 'Chandler',
    date: '2012/08/06',
    salary: '$137,500',
  },

  {
    id: 24,
    name: 'Hope',
    position: 'Secretary',
    office: 'San Francisco',
    lastName: 'Fuentes',
    date: '2010/02/12',
    salary: '$109,850',
  },
  {
    id: 25,
    name: 'Howard',
    position: 'Office Manager',
    office: 'San Francisco',
    lastName: 'Hatfield',
    date: '2008/12/16',
    salary: '$164,500',
  },
  {
    id: 26,
    name: 'Jackson',
    position: 'Director',
    office: 'New York',
    lastName: 'Bradshaw',
    date: '2008/09/26',
    salary: '$645,750',
  },
  {
    id: 27,
    name: 'Jena',
    position: 'Office Manager',
    office: 'London',
    lastName: 'Gaines',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 28,
    name: 'Jenette',
    position: 'Development Lead',
    office: 'New York',
    lastName: 'Caldwell',
    date: '2011/09/03',
    salary: '$345,000',
  },
  {
    id: 29,
    name: 'Jennifer',
    position: 'Junior Javascript Developer',
    office: 'Edinburgh',
    lastName: 'Acosta',
    date: '2013/02/01',
    salary: '$75,650',
  },
  {
    id: 30,
    name: 'Bruno',
    position: 'Software Engineer',
    office: 'London',
    lastName: 'Nash',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 31,
    name: 'Caesar',
    position: 'Pre-Sales Support',
    office: 'New York',
    lastName: 'Vance',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 32,
    name: 'Cara',
    position: 'Sales Assistant',
    office: 'New York',
    lastName: 'Stevens',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 33,
    name: 'Cedric',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    lastName: 'Kelly',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 34,
    name: 'Charde',
    position: 'Regional Director',
    office: 'San Francisco',
    lastName: 'Marshall',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 35,
    name: 'Archana',
    position: 'Accountant',
    office: 'Tokyo',
    lastName: 'Desai',
    date: '2008/11/28',
    salary: '$162,700',
  },
  {
    id: 36,
    name: 'Ashton',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    lastName: 'Cox',
    date: '2009/01/12',
    salary: '$86,000',
  },
  {
    id: 37,
    name: 'Bradley',
    position: 'Software Engineer',
    office: 'London',
    lastName: 'Greer',
    date: '2012/10/13',
    salary: '$132,000',
  },
  {
    id: 38,
    name: 'Brenden',
    position: 'Software Engineer',
    office: 'San Francisco',
    lastName: 'Wagner',
    date: '2011/06/07',
    salary: '$206,850',
  },
  {
    id: 39,
    name: 'Bruno',
    position: 'Software Engineer',
    office: 'London',
    lastName: 'Nash',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 40,
    name: 'Herrod',
    position: 'Sales Assistant',
    office: 'San Francisco',
    lastName: 'Chandler',
    date: '2012/08/06',
    salary: '$137,500',
  },
  {
    id: 41,
    name: 'Jena',
    position: 'Office Manager',
    office: 'London',
    lastName: 'Gaines',
    date: '2008/12/19',
    salary: '$90,560',
  },
  {
    id: 42,
    name: 'Gloria',
    position: 'Systems Administrator',
    office: 'New York',
    lastName: 'Little',
    date: '2009/04/10',
    salary: '$237,500',
  },
  {
    id: 43,
    name: 'Haley',
    position: 'Senior Marketing Designer',
    office: 'London',
    lastName: 'Kennedy',
    date: '2012/12/18',
    salary: '$313,500',
  },
  {
    id: 44,
    name: 'Hermione',
    position: 'Regional Director',
    office: 'London',
    lastName: 'Butler',
    date: '2011/03/21',
    salary: '$356,250',
  },
  {
    id: 45,
    name: 'Cedric',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    lastName: 'Kelly',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 46,
    name: 'Charde',
    position: 'Regional Director',
    office: 'San Francisco',
    lastName: 'Marshall',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 47,
    name: 'Dai',
    position: 'Personnel Lead',
    office: 'Edinburgh',
    lastName: 'Rios',
    date: '2012/09/26',
    salary: '$217,500',
  },
  {
    id: 48,
    name: 'Doris',
    position: 'Sales Assistant',
    office: 'Sidney',
    lastName: 'Wilder',
    date: '2010/09/20',
    salary: '$85,600',
  },
  {
    id: 49,
    name: 'Fiona',
    position: 'Chief Executive Officer (CEO)',
    office: 'San Francisco',
    lastName: 'Green',
    date: '2010/03/11',
    salary: '$850,000',
  },
  {
    id: 50,
    name: 'Jennifer',
    position: 'Junior Javascript Developer',
    office: 'Edinburgh',
    lastName: 'Acosta',
    date: '2013/02/01',
    salary: '$75,650',
  },
  {
    id: 51,
    name: 'Bruno',
    position: 'Software Engineer',
    office: 'London',
    lastName: 'Nash',
    date: '2011/05/03',
    salary: '$163,500',
  },
  {
    id: 52,
    name: 'Caesar',
    position: 'Pre-Sales Support',
    office: 'New York',
    lastName: 'Vance',
    date: '2011/12/12',
    salary: '$106,450',
  },
  {
    id: 53,
    name: 'Cara',
    position: 'Sales Assistant',
    office: 'New York',
    lastName: 'Stevens',
    date: '2011/12/06',
    salary: '$145,600',
  },
  {
    id: 54,
    name: 'Cedric',
    position: 'Senior Javascript Developer',
    office: 'Edinburgh',
    lastName: 'Kelly',
    date: '2012/03/29',
    salary: '$433,060',
  },
  {
    id: 55,
    name: 'Charde',
    position: 'Regional Director',
    office: 'San Francisco',
    lastName: '6741',
    date: '2008/10/16',
    salary: '$470,600',
  },
  {
    id: 56,
    name: 'Archana',
    position: 'Accountant',
    office: 'Tokyo',
    lastName: 'Desai',
    date: '2008/11/28',
    salary: '$162,700',
  },
  {
    id: 57,
    name: 'Ashton',
    position: 'Junior Technical Author',
    office: 'San Francisco',
    lastName: 'Cox',
    date: '2009/01/12',
    salary: '$86,000',
  },
];
